import React, { useState, useEffect, useCallback, useRef, memo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  Edit3, 
  Play, 
  Share2, 
  Plus, 
  Save, 
  Trash2,
  Image,
  RefreshCw,
  MessageSquare,
  Settings,
  Eye,
  Brain,
  FolderOpen,
  RotateCcw,
  Download,
  Sparkles,
  ArrowLeft,
  Camera
} from 'lucide-react'
import localCloudService from '../services/localCloudService'
import enhancedStorageService from '../services/enhancedStorageService'
import MediaLibrary from '../components/MediaLibrary'
import DataRecoveryModal from '../components/DataRecoveryModal'
import RecycleBinModal from '../components/RecycleBinModal'
import ImportExportModal from '../components/ImportExportModal'
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  MarkerType,
  Handle,
  Position,
  useReactFlow
} from 'react-flow-renderer'
import { useStoryStore } from '../stores/storyStore'
import toast from 'react-hot-toast'
import settingsService from '../services/settingsService'
import { ImageManager } from '../utils/imageManager.js'
import { calculateNodePositions } from '../utils/storyLayoutAlgorithm.js'



// 自定义节点组件 - 简化版本
const StoryNode = ({ data, selected, id }) => {
  const [showContextMenu, setShowContextMenu] = useState(false)
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 })
  const [imageError, setImageError] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [imageKey, setImageKey] = useState(0) // 用于强制重新渲染图片
  const nodeRef = useRef(null)
  
  // 确保有正确的节点ID
  const nodeId = id || data?.id
  
  // 监听图片数据变化，强制重新渲染
  useEffect(() => {
    const imageUrl = data.sceneImage || data.backgroundImage
    if (imageUrl) {
      console.log(`🔄 节点 ${nodeId} 图片数据变化:`, imageUrl)
      setImageKey(prev => prev + 1) // 强制重新渲染图片
      setImageError(false) // 重置错误状态
      setRetryCount(0) // 重置重试计数
    }
  }, [data.sceneImage, data.backgroundImage, nodeId])
  
  // 图片重试逻辑
  const handleImageError = async (event) => {
    const imageUrl = data.sceneImage || data.backgroundImage
    console.log(`❌ 图片加载失败，尝试重试 (${retryCount + 1}/3):`, {
      imageUrl,
      nodeId,
      error: event.nativeEvent,
      retryCount
    })
    setImageError(true)
    
    if (retryCount < 3) {
      if (imageUrl && imageUrl.startsWith('blob:')) {
        try {
          // 从图片URL中提取文件名
          const urlParts = imageUrl.split('/')
          const fileName = urlParts[urlParts.length - 1]
          
          console.log(`🔄 尝试从IndexedDB恢复图片: ${fileName}`)
          
          // 尝试从IndexedDB重新获取图片
          const restoredUrl = await localCloudService.getImageUrl(fileName)
          if (restoredUrl) {
            console.log(`✅ 图片重试成功: ${restoredUrl}`)
            setImageError(false)
            setRetryCount(0)
            // 更新节点数据
            if (data.sceneImage) {
              data.sceneImage = restoredUrl
            } else if (data.backgroundImage) {
              data.backgroundImage = restoredUrl
            }
            return
          } else {
            console.warn(`⚠️ 无法从IndexedDB恢复图片: ${fileName}`)
          }
        } catch (error) {
          console.error('图片重试失败:', error)
        }
      } else {
        console.warn(`⚠️ 图片URL不是blob格式，无法重试: ${imageUrl}`)
      }
      
      setRetryCount(prev => prev + 1)
      // 延迟重试
      setTimeout(() => {
        setImageError(false)
      }, 1000)
    } else {
      console.warn('图片重试次数已达上限，停止重试')
    }
  }
  
  // 事件处理函数
  const handleContextMenu = (event) => {
    event.preventDefault()
    const rect = nodeRef.current.getBoundingClientRect()
    setContextMenuPosition({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    })
    setShowContextMenu(true)
  }

  const handleClick = () => {
    setShowContextMenu(false)
  }

  return (
    <div 
      ref={nodeRef}
      className={`relative p-4 rounded-2xl border-2 transition-all duration-300 w-[300px] ${
        selected 
          ? 'border-cyan-500 bg-black/60 backdrop-blur-xl shadow-2xl shadow-cyan-500/30' 
          : 'border-white/20 bg-black/40 backdrop-blur-xl'
      }`}
      onContextMenu={handleContextMenu}
      onClick={handleClick}
      style={{
        cursor: 'grab' // 添加拖拽光标
      }}
    >
      {/* 左侧输入连接点 */}
      <Handle
        type="target"
        position={Position.Left}
        className="w-5 h-5 bg-gray-400 border-2 border-dark-800 hover:bg-gray-300 cursor-crosshair"
        style={{ 
          top: '50%', 
          transform: 'translateY(-50%)',
          pointerEvents: 'all' // 确保连接点可以点击
        }}
      />

      {/* 拖拽区域 - 确保整个节点可以拖拽 */}
      <div 
        className="absolute inset-0 z-10"
        style={{
          pointerEvents: 'none' // 让拖拽事件穿透到ReactFlow
        }}
      />
      
      {/* 节点头部 */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-white text-sm">{data.title || '未命名镜头'}</h4>
        <div className="flex items-center gap-1">
          {(data.hasImage || data.sceneImage || data.backgroundImage) && <Image size={14} className="text-primary-400" />}
          {data.hasDialogue && <MessageSquare size={14} className="text-primary-400" />}
        </div>
      </div>
      
      {/* 镜头图 */}
      <div className="mb-3">
        {(() => {
          const hasImage = !!(data.sceneImage || data.backgroundImage)
          // console.log(`🔍 节点 ${data.title} 图片检查:`, {
          //   sceneImage: data.sceneImage,
          //   backgroundImage: data.backgroundImage,
          //   hasImage: hasImage,
          //   imageFixed: data.imageFixed,
          //   imageSource: data.imageSource
          // })
          
          return hasImage ? (
            <div className="relative w-full h-32 bg-dark-600 rounded overflow-hidden">
              {!imageError ? (
                <img 
                  key={`${data.sceneImage || data.backgroundImage}-${imageKey}`}
                  src={data.sceneImage || data.backgroundImage} 
                  alt="镜头图" 
                  className="w-full h-full object-cover"
                  onLoad={() => {
                    console.log(`✅ 图片加载成功: ${data.sceneImage || data.backgroundImage}`)
                    setImageError(false)
                    setRetryCount(0)
                  }}
                  onError={handleImageError}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-dark-700">
                  <div className="text-center text-dark-400">
                    <Image size={24} className="mx-auto mb-1" />
                    <p className="text-xs">图片加载中...</p>
                    <p className="text-xs">重试 {retryCount}/3</p>
                  </div>
                </div>
              )}
              <div className="absolute top-1 right-1 bg-black bg-opacity-50 rounded px-1 py-0.5">
                <span className="text-xs text-white">镜头图</span>
              </div>
            </div>
          ) : (
            <div className="w-full h-32 bg-white/5 rounded-xl flex items-center justify-center border-2 border-dashed border-white/20">
              <div className="text-center text-dark-400">
                <Image size={24} className="mx-auto mb-1" />
                <p className="text-xs">No image</p>
                <p className="text-xs">右键设置镜头图</p>
              </div>
            </div>
          )
        })()}
      </div>
      
      {/* 镜头描述预览 */}
      <div className="text-xs text-gray-300 mb-3">
        <p className="line-clamp-2">{data.description || '点击编辑镜头内容'}</p>
      </div>
      
      {/* 分支选项 - 只在有分支时显示 */}
      {data.branches && data.branches.length > 0 && (
        <div className="mb-3">
          <div className="text-xs text-gray-400 mb-2">分支选项:</div>
          <div className="space-y-1">
            {data.branches.map((branch, index) => (
              <div key={branch.id} className="relative text-xs bg-white/10 px-2 py-1 rounded-lg">
                {branch.text || `选项${index + 1}`}
                {/* 分支左侧连接点 - 放在选项文字左侧 */}
                <Handle
                  type="target"
                  position={Position.Left}
                  id={`branch-left-${branch.id}`}
                  className="w-4 h-4 bg-orange-500 border-2 border-dark-800 hover:bg-orange-400 cursor-crosshair"
                  style={{ 
                    top: '50%',
                    left: '-8px',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'all'
                  }}
                />
                {/* 分支右侧连接点 - 放在选项文字右侧 */}
                <Handle
                  type="source"
                  position={Position.Right}
                  id={`branch-right-${branch.id}`}
                  className="w-4 h-4 bg-green-500 border-2 border-dark-800 hover:bg-green-400 cursor-crosshair"
                  style={{ 
                    top: '50%',
                    right: '-8px',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'all'
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* 节点底部信息 */}
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-400">
          分支: {data.branches?.length || 0}
        </span>
        <span className={`px-2 py-1 rounded-full text-xs ${
          data.type === 'start' ? 'bg-green-600' :
          data.type === 'ending' ? 'bg-red-600' :
          'bg-blue-600'
        }`}>
          {data.type === 'start' ? '开始' : data.type === 'ending' ? '结局' : '镜头'}
        </span>
      </div>

      {/* 右侧输出连接点 */}
      {(!data.branches || data.branches.length === 0) ? (
        // 如果没有分支选项，显示一个通用的输出连接点
        <Handle
          type="source"
          position={Position.Right}
          className="w-5 h-5 bg-blue-500 border-2 border-dark-800 hover:bg-blue-400 cursor-crosshair"
          style={{ 
            top: '50%', 
            transform: 'translateY(-50%)',
            pointerEvents: 'all'
          }}
        />
      ) : (
        // 如果有分支选项，禁用原有连接点
        <Handle
          type="source"
          position={Position.Right}
          className="w-5 h-5 bg-gray-500 border-2 border-dark-800 cursor-not-allowed opacity-50"
          style={{ top: '50%', transform: 'translateY(-50%)' }}
          disabled={true}
        />
      )}

      {/* 右键菜单 */}
      {showContextMenu && (
        <div 
          className="absolute z-50 bg-black/80 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl py-2 min-w-[200px]"
          style={{
            top: contextMenuPosition.y,
            left: contextMenuPosition.x
          }}
        >
          <button 
            className="w-full text-left px-4 py-2 text-sm text-white hover:bg-white/10 flex items-center gap-2 rounded-lg transition-colors duration-200"
            onClick={() => {
              console.log('点击编辑镜头按钮，节点ID:', nodeId)
              setShowContextMenu(false)
              // 触发编辑事件
              const event = new CustomEvent('editNode', { detail: { nodeId: nodeId } })
              console.log('派发编辑事件:', event)
              window.dispatchEvent(event)
            }}
          >
            <Edit3 size={14} />
            编辑镜头
          </button>
          <button 
            className="w-full text-left px-4 py-2 text-sm text-white hover:bg-dark-600 flex items-center gap-2"
            onClick={() => {
              setShowContextMenu(false)
              // 触发图片设置事件
              const event = new CustomEvent('setSceneImage', { detail: { nodeId: nodeId } })
              window.dispatchEvent(event)
            }}
          >
            <Image size={14} />
            设置镜头图
          </button>
          <button 
            className="w-full text-left px-4 py-2 text-sm text-white hover:bg-dark-600 flex items-center gap-2"
            onClick={() => {
              setShowContextMenu(false)
              // 触发分支设置事件
              const event = new CustomEvent('setBranches', { detail: { nodeId: nodeId } })
              window.dispatchEvent(event)
            }}
          >
            <MessageSquare size={14} />
            设置分支选项
          </button>
          <button 
            className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-600 hover:text-white flex items-center gap-2"
            onClick={() => {
              console.log('点击删除镜头按钮，节点ID:', nodeId)
              setShowContextMenu(false)
              // 触发删除事件
              const event = new CustomEvent('deleteNode', { detail: { nodeId: nodeId } })
              console.log('派发删除事件:', event)
              window.dispatchEvent(event)
            }}
          >
            <Trash2 size={14} />
            删除镜头
          </button>
        </div>
      )}
    </div>
  )
}

// 自定义边组件 - 线性形式，带选中高亮
const CustomEdge = ({ id, sourceX, sourceY, targetX, targetY, style = {}, selected }) => {
  const handleDelete = (e) => {
    e.stopPropagation()
    // 触发删除事件
    const event = new CustomEvent('disconnectEdge', { detail: { edgeId: id } })
    window.dispatchEvent(event)
  }
  
  // 计算连线中点
  const midX = (sourceX + targetX) / 2
  const midY = (sourceY + targetY) / 2
  
  return (
    <g>
      {/* 增加可点击区域的透明路径 */}
      <path
        id={`${id}-clickable`}
        style={{
          stroke: 'transparent',
          strokeWidth: 20, // 增加可点击区域
          fill: 'none',
          cursor: 'pointer'
        }}
        d={`M ${sourceX} ${sourceY} L ${targetX} ${targetY}`}
      />
      
      {/* 连线路径 - 线性形式 */}
      <path
        id={id}
        style={{
          ...style,
          stroke: selected ? '#10B981' : '#3B82F6', // 选中时绿色，未选中时蓝色
          strokeWidth: selected ? 8 : 6, // 选中时更粗，未选中时也加粗
          strokeOpacity: selected ? 1 : 0.8,
          filter: selected ? 'drop-shadow(0 0 8px #10B981)' : 'none', // 选中时发光效果
          zIndex: 1000, // 确保连线在节点之上
          pointerEvents: 'none' // 让透明路径处理点击事件
        }}
        className="react-flow__edge-path"
        d={`M ${sourceX} ${sourceY} L ${targetX} ${targetY}`} // 直线连接
      />
      
      {/* 删除按钮 - 始终显示，替代蓝色长方形 */}
      <g>
        {/* 删除按钮背景 - 矩形样式，更明显 */}
        <rect
          x={midX - 15}
          y={midY - 8}
          width={30}
          height={16}
          rx={4}
          fill="#ef4444"
          stroke="#dc2626"
          strokeWidth={1}
          onClick={handleDelete}
          style={{ 
            cursor: 'pointer',
            pointerEvents: 'all',
            opacity: 0.9
          }}
        />
        {/* 删除按钮文字 */}
        <text
          x={midX}
          y={midY + 2}
          textAnchor="middle"
          fill="white"
          fontSize="10"
          fontWeight="bold"
          onClick={handleDelete}
          style={{ 
            cursor: 'pointer', 
            userSelect: 'none',
            pointerEvents: 'all'
          }}
        >
          删除
        </text>
      </g>
    </g>
  )
}

// 画布交互处理组件 - 必须在 ReactFlow 内部使用
const CanvasInteractionHandler = ({ onDoubleClick, instanceRef, onInteractionStart }) => {
  const reactFlowInstance = useReactFlow()
  
  // 保存实例引用
  useEffect(() => {
    if (instanceRef) {
      instanceRef.current = reactFlowInstance
    }
  }, [reactFlowInstance, instanceRef])
  
  useEffect(() => {
    // 双击处理 - 放大并居中
    const handleDoubleClick = (event) => {
      // 检查是否点击在空白画布上（不是节点或边）
      if (event.target.classList.contains('react-flow__pane')) {
        // 通知开始交互，关闭右键菜单
        if (onInteractionStart) {
          onInteractionStart()
        }
        
        const bounds = event.currentTarget.getBoundingClientRect()
        const x = event.clientX - bounds.left
        const y = event.clientY - bounds.top
        
        // 转换为流坐标
        const position = reactFlowInstance.project({ x, y })
        
        // 缩放并居中到双击位置
        reactFlowInstance.setCenter(position.x, position.y, { 
          zoom: Math.min(reactFlowInstance.getZoom() * 1.5, 2), // 放大1.5倍，最大2倍
          duration: 400 
        })
      }
    }
    
    // 任意交互时关闭右键菜单
    const handleInteraction = (event) => {
      if (onInteractionStart) {
        onInteractionStart()
      }
    }
    
    const wrapper = document.querySelector('.react-flow')
    const pane = document.querySelector('.react-flow__pane')
    
    if (wrapper && pane) {
      // 双击缩放
      pane.addEventListener('dblclick', handleDoubleClick)
      // 交互时关闭右键菜单
      wrapper.addEventListener('mousedown', handleInteraction)
      wrapper.addEventListener('wheel', handleInteraction, { passive: true })
      
      return () => {
        pane.removeEventListener('dblclick', handleDoubleClick)
        wrapper.removeEventListener('mousedown', handleInteraction)
        wrapper.removeEventListener('wheel', handleInteraction)
      }
    }
  }, [reactFlowInstance, onInteractionStart])
  
  return null
}

export const StoryEditor = () => {
  const { storyId } = useParams()
  const navigate = useNavigate()
  const { stories, currentStory, setCurrentStory, updateStory } = useStoryStore()
  
  // 节点类型配置 - 使用useMemo优化
  const nodeTypes = React.useMemo(() => ({
    storyNode: StoryNode
  }), [])

  // 边类型配置 - 使用useMemo优化
  const edgeTypes = React.useMemo(() => ({
    default: CustomEdge
  }), [])
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])
  const [showNodeEditor, setShowNodeEditor] = useState(false)
  const [selectedNode, setSelectedNode] = useState(null)
  const [selectedEdge, setSelectedEdge] = useState(null)
  const [showCharacterEditor, setShowCharacterEditor] = useState(false)
  const [showChapterManager, setShowChapterManager] = useState(false)
  const [showSceneManager, setShowSceneManager] = useState(false)
  const [showMediaLibrary, setShowMediaLibrary] = useState(false)
  const [showDataRecovery, setShowDataRecovery] = useState(false)
  const [showRecycleBin, setShowRecycleBin] = useState(false)
  const [showImportExport, setShowImportExport] = useState(false)
  const [canvasContextMenu, setCanvasContextMenu] = useState({ show: false, position: { x: 0, y: 0 }, flowPosition: { x: 0, y: 0 } })
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [nodeToDelete, setNodeToDelete] = useState(null)
  const reactFlowWrapper = useRef(null)
  const reactFlowInstance = useRef(null)

  // 使用 useRef 来跟踪是否已经初始化过
  const initializedRef = useRef(false)
  
  // 分析故事结构，创建节点和连接信息
  const analyzeStoryStructure = (scenes) => {
    console.log('analyzeStoryStructure 输入镜头:', scenes)
    
    const structure = {
      nodes: [],
      connections: []
    }
    
    // 为每个镜头创建节点信息
    scenes.forEach((scene, index) => {
      const nodeId = scene.id || `scene-${scene.chapter}-${scene.scene}`
      
      // 规范化选择格式，确保每个选择都有必要的字段
      const normalizedChoices = (scene.choices || []).map((choice, choiceIndex) => ({
        id: choice.id || `choice-${scene.chapter}-${scene.scene}-${choiceIndex + 1}`,
        text: choice.text || choice.content || `选择${choiceIndex + 1}`,
        targetNodeId: choice.targetNodeId || choice.targetSceneId || choice.nextScene || null,
        path: choice.path || `path-${choiceIndex + 1}`,
        consequence: choice.consequence || `选择了"${choice.text || `选择${choiceIndex + 1}`}"`
      }))
      
      structure.nodes.push({
        id: nodeId,
        scene: {
          ...scene,
          choices: normalizedChoices // 使用规范化的选择
        },
        isBranch: normalizedChoices.length > 0,
        choices: normalizedChoices
      })
      
      console.log(`创建节点 ${index + 1}: ${nodeId} (${scene.title})`)
      if (normalizedChoices.length > 0) {
        console.log(`  包含 ${normalizedChoices.length} 个选择:`, normalizedChoices.map(c => c.text))
      }
    })
    
    // 基于故事分支逻辑创建连接线
    console.log('\n=== 开始基于故事逻辑创建连接线 ===')
    
    // 0. 首先诊断所有场景的ID和choices配置
    console.log('\n📋 场景ID和选择配置诊断:')
    const sceneIdMap = new Map()
    scenes.forEach((scene, idx) => {
      const sceneId = scene.id || `scene-${scene.chapter}-${scene.scene}`
      sceneIdMap.set(sceneId, scene)
      
      if (scene.choices && scene.choices.length > 0) {
        console.log(`  场景 ${idx + 1}: ${sceneId} (${scene.title})`)
        console.log(`    有 ${scene.choices.length} 个选择:`)
        scene.choices.forEach((choice, cIdx) => {
          const targetId = choice.targetNodeId || choice.targetSceneId || choice.nextScene
          const targetExists = targetId && scenes.some(s => s.id === targetId)
          console.log(`      选择 ${cIdx + 1}: "${choice.text}"`)
          console.log(`        -> targetNodeId: ${choice.targetNodeId || 'null'}`)
          console.log(`        -> targetSceneId: ${choice.targetSceneId || 'null'}`)
          console.log(`        -> nextScene: ${choice.nextScene || 'null'}`)
          console.log(`        -> 最终目标: ${targetId || '❌ 未设置'}`)
          console.log(`        -> 目标存在: ${targetExists ? '✅' : '❌'}`)
        })
      }
    })
    console.log(`\n📊 场景ID映射: 共 ${sceneIdMap.size} 个场景`)
    console.log('可用的场景ID:', Array.from(sceneIdMap.keys()))
    
    // 1. 智能分析场景类型和连接需求
    const sceneAnalysis = scenes.map((scene, index) => {
      const hasChoices = scene.choices && scene.choices.length > 0
      const isLinear = !hasChoices
      const isBranch = hasChoices && scene.choices.length >= 2
      const isDecision = hasChoices && scene.choices.length === 1 // 单一选择，可能是关键决策
      
      return {
        scene,
        index,
        hasChoices,
        isLinear,
        isBranch,
        isDecision,
        nodeId: scene.id || `scene-${scene.chapter}-${scene.scene}`
      }
    })
    
    console.log('📊 场景分析结果:', sceneAnalysis.map(s => ({
      title: s.scene.title,
      type: s.isBranch ? '分支' : s.isDecision ? '决策' : s.isLinear ? '线性' : '未知',
      hasChoices: s.hasChoices,
      choicesCount: s.scene.choices?.length || 0,
      choicesData: s.scene.choices,
      isBranch: s.isBranch,
      isLinear: s.isLinear
    })))
    
    // 2. 创建分支连接：基于有意义的选择
    sceneAnalysis.forEach(({ scene, nodeId, isBranch, isDecision }) => {
      if (isBranch || isDecision) {
        console.log(`\n处理${isBranch ? '分支' : '决策'}场景: ${scene.title} (${nodeId})`)
        
        // 处理所有分支选项，不再限制数量
        const allChoices = scene.choices || []
        console.log(`  处理 ${allChoices.length} 个分支选项`)
        
        allChoices.forEach((choice, choiceIndex) => {
          console.log(`  选择 ${choiceIndex + 1}: "${choice.text}" -> 目标: ${choice.targetNodeId}`)
          
          if (choice.targetNodeId) {
            const targetScene = scenes.find(s => s.id === choice.targetNodeId)
            if (targetScene) {
              const targetNodeId = targetScene.id || `scene-${targetScene.chapter}-${targetScene.scene}`
              console.log(`    找到目标场景: ${targetScene.title} (${targetScene.id})`)
              
              // 检查已存在连接，支持任意数量的分支连接
              const existingConnection = structure.connections.find(conn => 
                conn.from === nodeId && conn.to === targetNodeId
              )
              
              if (!existingConnection) {
                structure.connections.push({
                  id: `branch-${scene.id}-${choiceIndex}`,
                  from: nodeId,
                  fromHandle: `branch-right-${choice.id}`,
                  to: targetNodeId,
                  toHandle: null,
                  type: 'branch',
                  label: choice.text,
                  choice: choice,
                  path: choice.path || `branch-${choiceIndex}`
                })
                console.log(`    ✓ 创建分支连接: ${nodeId} -> ${targetNodeId}`)
              } else {
                console.log(`    - 连接已存在，跳过: ${nodeId} -> ${targetNodeId}`)
              }
            } else {
              console.warn(`    ✗ 未找到目标场景: ${choice.targetNodeId}`)
            }
          } else {
            console.warn(`    ✗ 选择 ${choiceIndex} 没有目标场景ID`)
          }
        })
      }
    })
    
    // 3. 创建线性连接：连接没有分支的场景（但排除结局节点）
    sceneAnalysis.forEach(({ scene, nodeId, isLinear }, index) => {
      if (isLinear) {
        // 🔧 修复：结局节点判断逻辑 - 只识别 E 开头的节点作为结局
        const isEndingNode = /^E\d+$/.test(nodeId) || 
                            (scene.type === 'ending' && scene.isEnding === true)
        
        if (isEndingNode) {
          console.log(`⏹️ 跳过结局节点的线性连接: ${nodeId}`)
          return // 结局节点不创建出边
        }
        
        // 🆕 添加诊断日志
        if (scene.isEnding === true && !/^E\d+$/.test(nodeId)) {
          console.warn(`⚠️ 警告: 节点 ${nodeId} 被标记为 isEnding=true，但 sceneId 不符合 E 系列规范！`)
          console.warn(`   节点信息:`, {
            sceneId: nodeId,
            type: scene.type,
            isEnding: scene.isEnding,
            title: scene.title
          })
        }
        
        // 根据节点类型智能查找下一个节点
        let nextScene = null
        
        // M系列节点 -> 查找下一个M节点
        if (/^M\d+$/.test(nodeId)) {
          const mainIndex = parseInt(nodeId.match(/^M(\d+)$/)[1])
          nextScene = scenes.find(s => s.id === `M${mainIndex + 1}`)
          if (nextScene) {
            console.log(`  M系列连接: ${nodeId} -> M${mainIndex + 1}`)
          }
        }
        // L系列节点 -> 查找同一支线的下一个节点（支持多级：L1-1, L2-1-1, L2-2-1等）
        else if (/^L\d+(?:-\d+)+$/.test(nodeId)) {
          // 优先1：检查是否有子节点（如 L2-1 → L2-1-1）
          const childNode = scenes.find(s => s.id === `${nodeId}-1`)
          
          if (childNode) {
            nextScene = childNode
            console.log(`  L系列子节点连接: ${nodeId} -> ${nodeId}-1`)
          } else {
            // 优先2：查找同级下一个节点
            const lastDashIndex = nodeId.lastIndexOf('-')
            const prefix = nodeId.substring(0, lastDashIndex)
            const lastNumber = parseInt(nodeId.substring(lastDashIndex + 1))
            
            nextScene = scenes.find(s => s.id === `${prefix}-${lastNumber + 1}`)
            if (nextScene) {
              console.log(`  L系列同级连接: ${nodeId} -> ${prefix}-${lastNumber + 1}`)
            }
          }
        }
        // 兜底：按章节和场景顺序查找
        else {
          nextScene = scenes.find(s => 
            s.chapter === scene.chapter && s.scene === scene.scene + 1
          ) || scenes.find(s => 
            s.chapter === scene.chapter + 1 && s.scene === 1
          )
        }
        
        if (nextScene) {
          const nextNodeId = nextScene.id || `scene-${nextScene.chapter}-${nextScene.scene}`
          
          // 🔧 修复：再次检查目标节点不是结局节点 - 只识别 E 开头的节点
          const nextIsEnding = /^E\d+$/.test(nextNodeId) || 
                              (nextScene.type === 'ending' && nextScene.isEnding === true)
          
          // 检查是否已经存在连接
          const existingConnection = structure.connections.find(conn => 
            conn.from === nodeId && conn.to === nextNodeId
          )
          
          if (!existingConnection && !nextIsEnding) {
            structure.connections.push({
              id: `linear-${nodeId}-to-${nextNodeId}`,
              from: nodeId,
              to: nextNodeId,
              type: 'linear',
              label: '继续',
              path: 'linear'
            })
            
            console.log(`✓ 创建线性连接: ${nodeId} -> ${nextNodeId}`)
          } else if (nextIsEnding) {
            console.log(`⏹️ 目标是结局节点，跳过: ${nodeId} -> ${nextNodeId}`)
          }
        }
      }
    })
    
    // 3.5. 【调整】M系列节点补充连接：仅在"非决策/无choices"的线性节点时补充
    console.log('\n📝 M系列节点补充连接...')
    sceneAnalysis.forEach(({ scene, nodeId }) => {
      if (/^M\d+$/.test(nodeId)) {
        // 🔧 修复：结局节点判断 - 只识别 E 开头的节点
        const isEndingNode = /^E\d+$/.test(nodeId) || 
                            (scene.type === 'ending' && scene.isEnding === true)
        const hasChoices = Array.isArray(scene.choices) && scene.choices.length > 0
        if (isEndingNode) return
        if (hasChoices) return // 决策节点不自动补主线连接，由分支选择决定流向
        
        const mainIndex = parseInt(nodeId.match(/^M(\d+)$/)[1])
        const nextMScene = scenes.find(s => s.id === `M${mainIndex + 1}`)
        
        if (nextMScene) {
          const nextNodeId = nextMScene.id
          const existingConnection = structure.connections.find(conn => 
            conn.from === nodeId && conn.to === nextNodeId
          )
          
          if (!existingConnection) {
            structure.connections.push({
              id: `linear-M-${nodeId}-to-${nextNodeId}`,
              from: nodeId,
              to: nextNodeId,
              type: 'linear',
              label: '继续',
              path: 'linear'
            })
            console.log(`  ✓ M系列补充连接: ${nodeId} -> ${nextNodeId}`)
          }
        } else {
          // M系列最后一个节点，尝试连接到 E1
          const e1Scene = scenes.find(s => s.id === 'E1')
          if (e1Scene) {
            const existingConnection = structure.connections.find(conn => 
              conn.from === nodeId && conn.to === 'E1'
            )
            if (!existingConnection) {
              structure.connections.push({
                id: `linear-M-${nodeId}-to-E1`,
                from: nodeId,
                to: 'E1',
                type: 'linear',
                label: '继续',
                path: 'linear'
              })
              console.log(`  ✓ M系列到E1结局: ${nodeId} -> E1`)
            }
          }
        }
      }
    })
    
    // 3.6. 【新增】桥接节点到结局的连接（处理 {nodeId}-to-E{n} 和 {nodeId}-end 模式）
    console.log('\n📝 桥接节点到结局的连接...')
    sceneAnalysis.forEach(({ scene, nodeId }) => {
      // 匹配桥接节点：M3-to-E2, L2-2-to-E3, L1-3-end 等
      const bridgeMatch = nodeId.match(/^(.+)-(?:to-(E\d+)|end)$/)
      if (bridgeMatch) {
        const sourceNodeId = bridgeMatch[1]  // M3, L2-2, L1-3
        const targetEndingId = bridgeMatch[2] // E2, E3, 或 undefined (如果是-end)
        
        console.log(`  发现桥接节点: ${nodeId}`)
        console.log(`    源节点: ${sourceNodeId}`)
        console.log(`    目标结局: ${targetEndingId || '需推断'}`)
        
        // 如果有明确的结局ID
        let endingId = targetEndingId
        
        // 如果是-end模式，需要推断结局
        if (!endingId) {
          if (/^M\d+$/.test(sourceNodeId)) {
            endingId = 'E1' // M系列 -> E1
          } else if (/^L\d+/.test(sourceNodeId)) {
            const topLevelMatch = sourceNodeId.match(/^L(\d+)/)
            if (topLevelMatch) {
              const branchNumber = parseInt(topLevelMatch[1])
              endingId = `E${branchNumber + 1}` // L1 -> E2, L2 -> E3
            }
          }
        }
        
        if (endingId) {
          const endingScene = scenes.find(s => s.id === endingId)
          if (endingScene) {
            const existingConnection = structure.connections.find(conn => 
              conn.from === nodeId && conn.to === endingId
            )
            
            if (!existingConnection) {
              structure.connections.push({
                id: `bridge-${nodeId}-to-${endingId}`,
                from: nodeId,
                to: endingId,
                type: 'linear',
                label: '继续',
                path: 'linear'
              })
              console.log(`    ✓ 桥接连接: ${nodeId} -> ${endingId}`)
            }
          } else {
            console.warn(`    ⚠️ 未找到结局节点: ${endingId}`)
          }
        }
      }
    })
    
    // 3.7. 【新增】L系列到对应结局的连接
    console.log('\n📝 L系列到结局的连接...')
    sceneAnalysis.forEach(({ scene, nodeId }) => {
      if (/^L\d+(?:-\d+)+$/.test(nodeId)) {
        // 提取顶级分支编号（L1, L2, L3）和节点完整前缀
        const topLevelMatch = nodeId.match(/^(L\d+)/)
        if (!topLevelMatch) return
        
        const branchSeries = topLevelMatch[1] // L1, L2, L3...
        
        // 提取前缀和最后的数字，检查是否有下一个节点
        const lastDashIndex = nodeId.lastIndexOf('-')
        const prefix = nodeId.substring(0, lastDashIndex)
        const lastNumber = parseInt(nodeId.substring(lastDashIndex + 1))
        const nextInSameBranch = scenes.find(s => s.id === `${prefix}-${lastNumber + 1}`)
        
        // 检查是否有子节点
        const hasChildNode = scenes.find(s => s.id === `${nodeId}-1`)
        
        // 如果没有下一个节点且没有子节点，说明这是支线的最后一个节点
        if (!nextInSameBranch && !hasChildNode) {
          // 计算对应的结局编号：L1 -> E2, L2 -> E3, L3 -> E4...
          const branchNumber = parseInt(branchSeries.replace('L', ''))
          const endingId = `E${branchNumber + 1}`
          
          const endingScene = scenes.find(s => s.id === endingId)
          if (endingScene) {
            const existingConnection = structure.connections.find(conn => 
              conn.from === nodeId && conn.to === endingId
            )
            
            if (!existingConnection) {
              structure.connections.push({
                id: `linear-L-${nodeId}-to-${endingId}`,
                from: nodeId,
                to: endingId,
                type: 'linear',
                label: '继续',
                path: 'linear'
              })
              console.log(`  ✓ ${branchSeries}到${endingId}结局: ${nodeId} -> ${endingId}`)
            }
          } else {
            console.warn(`  ⚠️ 未找到${branchSeries}的对应结局: ${endingId}`)
          }
        }
      }
    })
    
    // 4. 为缺少targetNodeId的选择创建智能连接
    sceneAnalysis.forEach(({ scene, nodeId, hasChoices }, index) => {
      if (hasChoices) {
        scene.choices.forEach((choice, choiceIndex) => {
          if (!choice.targetNodeId) {
            console.log(`⚠️ 选择 "${choice.text}" 缺少目标场景，创建智能连接`)
            
            // 智能推断目标场景：
            // 1. 优先连接到下一个章节的第一个场景
            const nextChapterScene = scenes.find(s => s.chapter === scene.chapter + 1 && s.scene === 1)
            // 2. 其次连接到同章节的下一个场景
            const nextScene = scenes.find(s => s.chapter === scene.chapter && s.scene === scene.scene + 1)
            // 3. 最后连接到故事中的任何后续场景
            const anyNextScene = scenes.find(s => s.id !== scene.id && index < scenes.length - 1)
            
            const targetScene = nextChapterScene || nextScene || anyNextScene
            
            if (targetScene) {
              const targetNodeId = targetScene.id || `scene-${targetScene.chapter}-${targetScene.scene}`
              
              // 检查是否已经存在连接
              const existingConnection = structure.connections.find(conn => 
                conn.from === nodeId && conn.to === targetNodeId && conn.choice?.id === choice.id
              )
              
              if (!existingConnection) {
                structure.connections.push({
                  id: `smart-branch-${scene.id}-${choiceIndex}`,
                  from: nodeId,
                  fromHandle: `branch-right-${choice.id}`,
                  to: targetNodeId,
                  toHandle: null,
                  type: 'branch',
                  label: choice.text,
                  choice: choice,
                  path: choice.path || `smart-branch-${choiceIndex}`,
                  autoGenerated: true // 标记为自动生成的连接
                })
                
                console.log(`✓ 创建智能分支连接: ${nodeId} -> ${targetNodeId} (${choice.text})`)
              }
            }
          }
        })
      }
    })
    
    // 4. 创建延迟汇合连接：处理分支汇合的情况
    const targetCounts = {}
    structure.connections.forEach(conn => {
      if (conn.to) {
        targetCounts[conn.to] = (targetCounts[conn.to] || 0) + 1
      }
    })
    
    // 标记汇聚点
    Object.entries(targetCounts).forEach(([targetNodeId, count]) => {
      if (count > 1) {
        console.log(`发现汇聚点: ${targetNodeId} (${count}个分支汇聚)`)
        
        // 更新连接类型为汇聚连接
        structure.connections.forEach(conn => {
          if (conn.to === targetNodeId) {
            conn.type = 'convergence'
            conn.label = conn.label || '汇合'
          }
        })
      }
    })
    
    // 4. 验证故事逻辑完整性
    console.log('\n=== 验证故事逻辑完整性 ===')
    const orphanedScenes = [] // 孤立场景（没有入边的非开始场景）
    scenes.forEach((scene, index) => {
      const sceneNodeId = scene.id || `scene-${scene.chapter}-${scene.scene}`
      
      // 检查这个场景是否有出边
      const outgoingConnections = structure.connections.filter(conn => conn.from === sceneNodeId)
      const hasOutgoingConnections = outgoingConnections.length > 0
      
      // 检查这个场景是否有入边
      const incomingConnections = structure.connections.filter(conn => conn.to === sceneNodeId)
      const hasIncomingConnections = incomingConnections.length > 0
      
      // 🔧 修复：结局场景判断逻辑 - 只识别 E 开头的节点或明确标记为结局的节点
      const isEndingScene = /^E\d+$/.test(sceneNodeId) || 
                           (scene.type === 'ending' && scene.isEnding === true)
      
      if (isEndingScene) {
        console.log(`✓ 结局场景: ${sceneNodeId} (${scene.title}) - 故事结束`)
        if (!hasIncomingConnections) {
          console.warn(`  ❌ 孤立结局！没有任何路径指向此结局`)
          orphanedScenes.push({
            id: sceneNodeId,
            title: scene.title,
            type: 'ending'
          })
        } else {
          console.log(`  ✅ 有 ${incomingConnections.length} 条入边:`, incomingConnections.map(c => `${c.from}(${c.label})`))
        }
        
        // 🆕 添加诊断：结局节点不应该有出边
        if (hasOutgoingConnections) {
          console.error(`  ❌ 错误: 结局节点 ${sceneNodeId} 不应该有出边连接！`)
          console.error(`     出边列表:`, outgoingConnections.map(c => `${c.to}(${c.label})`))
        }
      } else if (!hasOutgoingConnections && index < scenes.length - 1) {
        console.log(`⚠️  场景 ${sceneNodeId} (${scene.title}) 没有出边 - 可能是结局场景`)
      }
      
      // 🆕 添加诊断：非结局节点但被标记为结局
      if (!isEndingScene && scene.isEnding === true) {
        console.error(`❌ 错误: 节点 ${sceneNodeId} 被标记为 isEnding=true，但 sceneId 不符合 E 系列规范！`)
        console.error(`   应该使用 E1, E2, E3... 作为结局节点的 sceneId`)
        console.error(`   节点信息:`, {
          sceneId: sceneNodeId,
          type: scene.type,
          isEnding: scene.isEnding,
          title: scene.title
        })
      }
      
      if (!hasIncomingConnections && index > 0) {
        console.log(`⚠️  场景 ${sceneNodeId} (${scene.title}) 没有入边 - 可能是开始场景或孤立场景`)
        if (!isEndingScene) {
          orphanedScenes.push({
            id: sceneNodeId,
            title: scene.title,
            type: 'normal',
            choices: scene.choices?.length || 0
          })
        }
      }
    })
    
    if (orphanedScenes.length > 0) {
      console.error('\n❌ 发现 ' + orphanedScenes.length + ' 个孤立场景（无法到达）:')
      orphanedScenes.forEach(s => {
        console.error(`  - ${s.id} (${s.title}) [${s.type}]`)
      })
      console.log('💡 建议：检查AI生成的choices数据，确保所有分支的targetNodeId都正确指向目标场景')
    }
    
    // 5. 输出连接线统计
    console.log('\n=== 连接线统计 ===')
    const connectionTypes = {}
    structure.connections.forEach(conn => {
      connectionTypes[conn.type] = (connectionTypes[conn.type] || 0) + 1
    })
    
    Object.entries(connectionTypes).forEach(([type, count]) => {
      console.log(`${type}连接: ${count}条`)
    })
    
    console.log(`总连接数: ${structure.connections.length}`)
    
    // 6. 清理错误的连接：移除结局场景之间的连接
    const endingScenes = scenes.filter(scene => {
      const sceneNodeId = scene.id || `scene-${scene.chapter}-${scene.scene}`
      const hasOutgoingConnections = structure.connections.some(conn => conn.from === sceneNodeId)
      return (!scene.choices || scene.choices.length === 0) && !hasOutgoingConnections
    })
    
    const endingSceneIds = endingScenes.map(scene => scene.id || `scene-${scene.chapter}-${scene.scene}`)
    
    // 移除结局场景之间的连接
    structure.connections = structure.connections.filter(conn => {
      const isEndingToEnding = endingSceneIds.includes(conn.from) && endingSceneIds.includes(conn.to)
      if (isEndingToEnding) {
        console.log(`移除错误的结局连接: ${conn.from} -> ${conn.to}`)
        return false
      }
      return true
    })
    
    // 7. 输出所有连接详情
    console.log('\n=== 所有连接详情 ===')
    structure.connections.forEach((conn, index) => {
      console.log(`${index + 1}. ${conn.from} -> ${conn.to} [${conn.type}] "${conn.label}" (路径: ${conn.path})`)
    })
    
    console.log('\n=== 故事结构分析完成 ===')
    return structure
  }
  
  // 生成后备对话的辅助函数
  const generateFallbackDialogues = (scene, characters) => {
    const dialogues = []
    
    // 确保有可用的角色
    const availableCharacters = characters && characters.length > 0 
      ? characters 
      : [{ name: '旁白', role: 'narrator' }, { name: '主角', role: 'protagonist' }]
    
    // 基于场景内容生成2-3段对话
    const protagonist = availableCharacters.find(c => c.role === 'protagonist') || availableCharacters[0]
    const narrator = availableCharacters.find(c => c.role === 'narrator') || { name: '旁白', role: 'narrator' }
    
    // 第一段：旁白描述场景
    if (scene.content) {
      dialogues.push({
        character: narrator.name,
        content: scene.content.substring(0, 200) + (scene.content.length > 200 ? '...' : ''),
        emotion: 'neutral'
      })
    }
    
    // 第二段：主角反应
    dialogues.push({
      character: protagonist.name,
      content: scene.title.includes('选择') || scene.title.includes('决定') 
        ? '我需要仔细考虑这个选择...' 
        : '让我看看接下来会发生什么。',
      emotion: scene.mood === '紧张' ? 'nervous' : 'curious'
    })
    
    // 如果有其他角色，添加第三段对话
    if (availableCharacters.length > 2) {
      const supporting = availableCharacters.find(c => c.role === 'supporting') || availableCharacters[2]
      dialogues.push({
        character: supporting.name,
        content: '这里的情况看起来很有趣。',
        emotion: 'neutral'
      })
    }
    
    return dialogues
  }

  // 转换AI生成的角色对话格式为编辑器格式
  const convertDialoguesToEditorFormat = (aiDialogues, characters) => {
    // 处理空值情况
    if (!aiDialogues || !Array.isArray(aiDialogues) || aiDialogues.length === 0) {
      console.log('⚠️ 没有对话数据或对话数组为空')
      return []
    }
    
    if (!characters || !Array.isArray(characters)) {
      console.log('⚠️ 没有角色数据，使用空数组')
      characters = []
    }
    
    console.log('🔍 转换对话格式:', {
      aiDialoguesCount: aiDialogues.length,
      charactersCount: characters.length,
      sampleDialogue: aiDialogues[0],
      sampleCharacter: characters[0]
    })
    
    console.log('🔍 原始对话数据详情:', aiDialogues.map((d, i) => ({
      index: i,
      character: d.character,
      content: d.content?.substring(0, 50) + '...',
      allKeys: Object.keys(d)
    })))
    
    // 清理和过滤有效的对话数据
    const validDialogues = aiDialogues.filter(dialogue => {
      // 检查对话是否有有效的结构
      if (!dialogue || typeof dialogue !== 'object') {
        console.log('❌ 跳过非对象对话:', dialogue)
        return false
      }
      
      // 检查角色字段是否为有效的角色名（不是系统字段）
      const invalidCharacterNames = ['title', 'summary', 'mainPlot', 'mainConflict', 'role', 'description', 'content', 'choices', 'branches', 'characters', 'outline', 'chapters']
      if (!dialogue.character || invalidCharacterNames.includes(dialogue.character)) {
        console.log('❌ 跳过无效角色名对话:', dialogue.character)
        return false
      }
      
      // 检查是否有有效的对话内容
      if (!dialogue.content && !dialogue.text) {
        console.log('❌ 跳过无内容对话:', dialogue)
        return false
      }
      
      return true
    })
    
    console.log(`✅ 过滤后有效对话数: ${validDialogues.length}/${aiDialogues.length}`)
    
    // 如果过滤后没有有效对话，返回空数组（后续会生成后备对话）
    if (validDialogues.length === 0) {
      console.log('⚠️ 过滤后没有有效对话，将在场景处理中生成后备对话')
      return []
    }
    
    return validDialogues.map((dialogue, index) => {
      // 智能角色匹配：支持多种匹配方式
      let character = null
      
      // 方法1: 精确名称匹配
      character = characters.find(c => c.name === dialogue.character)
      
      // 方法2: 模糊匹配（包含关系）
      if (!character) {
        character = characters.find(c => 
          c.name.includes(dialogue.character) || 
          dialogue.character.includes(c.name)
        )
      }
      
      // 方法3: 角色类型匹配
      if (!character) {
        const roleMap = {
          '主角': 'protagonist',
          '引导者': 'mentor', 
          '对手': 'antagonist',
          '支持者': 'ally',
          '旁白': 'narrator'
        }
        const role = roleMap[dialogue.character]
        if (role) {
          character = characters.find(c => c.role === role)
        }
      }
      
      // 如果仍然找不到角色，创建一个临时角色（经过前面的过滤，这里的角色名应该都是有效的）
      if (!character) {
        console.log(`⚠️ 未找到角色 "${dialogue.character}"，创建临时角色`)
        character = {
          id: `temp-${dialogue.character}-${Date.now()}`,
          name: dialogue.character,
          role: 'supporting',
          description: `临时角色：${dialogue.character}`,
          languageStyle: '普通'
        }
      }
      
      return {
        id: `dialogue-${Date.now()}-${index}`,
        characterId: character.id,
        characterName: character.name,
        content: dialogue.text || dialogue.content || '',
        // 新增对话属性
        emotion: dialogue.emotion || 'neutral', // 情感状态
        order: index + 1, // 对话顺序
        isPlayerChoice: dialogue.isPlayerChoice || false, // 是否为玩家选择
        // 角色信息快照
        characterInfo: {
          role: character.role,
          languageStyle: character.languageStyle || '普通',
          personality: character.personality || ''
        }
      }
    }) // 无需额外过滤，前面已经过滤过了
  }

  // 验证和修复节点位置数据
  const validateAndFixNodePosition = (position) => {
    if (!position || typeof position !== 'object') {
      return { x: 200, y: 200 }
    }
    
    const x = typeof position.x === 'number' && !isNaN(position.x) ? position.x : 200
    const y = typeof position.y === 'number' && !isNaN(position.y) ? position.y : 200
    
    return { x, y }
  }

  // 验证和修复节点数据
  const validateAndFixNode = (node) => {
    if (!node) return null
    
    return {
      ...node,
      position: validateAndFixNodePosition(node.position),
      data: {
        ...node.data,
        // 确保所有必要字段都存在
        title: node.data?.title || '未命名场景',
        description: node.data?.description || '',
        type: node.data?.type || 'scene',
        content: node.data?.content || '',
        imagePrompt: node.data?.imagePrompt || '',
        sceneImage: node.data?.sceneImage || '',
        imageFixed: node.data?.imageFixed || false,
        imageSource: node.data?.imageSource || 'none',
        branches: Array.isArray(node.data?.branches) ? node.data.branches : [],
        dialogues: Array.isArray(node.data?.dialogues) ? node.data.dialogues : [],
        selectedCharacters: Array.isArray(node.data?.selectedCharacters) ? node.data.selectedCharacters : []
      }
    }
  }

  // ⚠️ calculateNodePositions 已移至 src/utils/storyLayoutAlgorithm.js 统一管理
  // 以下为旧代码，已废弃，请勿使用
  const calculateNodePositions_DEPRECATED = (storyStructure) => {
    const nodePositions = new Map()
    const nodeRows = new Map()
    const nodeCols = new Map()
    const horizontalSpacing = 450
    const verticalSpacing = 300
    const baseX = 200
    const baseY = 150

    console.log('🎨 主线-分支层级布局算法（新版）...')

    // 找到开始节点
    const startNode = storyStructure.nodes.find(n => 
      !storyStructure.connections.some(c => c.to === n.id)
    ) || storyStructure.nodes[0]

    if (!startNode) {
      console.warn('⚠️ 未找到开始节点')
      return nodePositions
    }

    console.log('🎯 开始节点:', startNode.id)

    // ========== 数据结构定义 ==========
    const nodeBranch = new Map() // nodeId -> branchName (如: 'M', 'L1', 'L2', 'L3')
    const branchNodes = new Map() // branchName -> [nodeIds]
    const branchParent = new Map() // branchName -> parentBranchName
    const branchChildren = new Map() // branchName -> [childBranchNames]
    const branchAppearOrder = [] // 分支出现的顺序

    // ========== 第一步：BFS遍历，识别主线和所有分支 ==========
    console.log('\n📝 第一步：识别主线和分支结构...')
    
    const queue = [{ nodeId: startNode.id, row: 0, col: 0, branchName: 'M' }]
    const visited = new Set()
    let nextLBranchNumber = 1 // L分支计数器

    // 初始化M主线
    branchNodes.set('M', [])
    branchParent.set('M', null)
    branchChildren.set('M', [])
    branchAppearOrder.push('M')

    while (queue.length > 0) {
      const { nodeId, row, col, branchName } = queue.shift()

      if (visited.has(nodeId)) continue
      visited.add(nodeId)

      // 记录节点归属
      nodeBranch.set(nodeId, branchName)
      branchNodes.get(branchName).push(nodeId)

      // 记录基准位置
      nodeRows.set(nodeId, row)
      nodeCols.set(nodeId, col)

      console.log(`  ${nodeId}: baseRow=${row}, col=${col}, branch=${branchName}`)

      // 查找子节点
      const childConnections = storyStructure.connections.filter(c => c.from === nodeId)

      if (childConnections.length === 0) {
        // 叶子节点
        continue
      } else if (childConnections.length === 1) {
        // 单一连接，继续当前分支
        const childNodeId = childConnections[0].to
        if (!visited.has(childNodeId)) {
          queue.push({
            nodeId: childNodeId,
            row: row,
            col: col + 1,
            branchName: branchName
          })
        }
      } else {
        // 分支点
        console.log(`  🔀 分支点 ${nodeId} (分支: ${branchName})`)
        
        childConnections.forEach((conn, index) => {
          const childNodeId = conn.to
          if (visited.has(childNodeId)) return

          // 优先检查是否是M系列或结局节点
          const isMainNode = /^M\d+$/.test(childNodeId)
          const isEndingNode = /^E\d+$/.test(childNodeId)
          
          if (isMainNode && branchName === 'M') {
            // M系列节点：继续M主线
            queue.push({
              nodeId: childNodeId,
              row: 0,
              col: col + 1,
              branchName: 'M'
            })
            console.log(`    ➡️ 继续M主线: ${childNodeId}`)
          } else if (isEndingNode) {
            // 结局节点：E1 属于主线，其余(E2+)属于对应的 L 分支
            const endingNum = parseInt(childNodeId.replace('E', ''))
            if (endingNum === 1 && branchName === 'M') {
              queue.push({ nodeId: childNodeId, row: 0, col: col + 1, branchName: 'M' })
              console.log('    🎬 主线结局 E1 放置在主线行 row 0')
            } else {
              // 将 E{n} 归属到 L{n-1} 分支（顶层），如果当前已在某个 L 分支，则保持在该分支
              let targetBranchName = branchName.startsWith('L') ? branchName : `L${Math.max(1, endingNum - 1)}`
              if (!branchNodes.has(targetBranchName)) {
                branchNodes.set(targetBranchName, [])
                branchParent.set(targetBranchName, 'M')
                if (!branchChildren.has('M')) branchChildren.set('M', [])
                branchChildren.get('M').push(targetBranchName)
                branchAppearOrder.push(targetBranchName)
                // 对齐分支计数器
                const lNumber = parseInt(targetBranchName.replace('L', ''))
                if (lNumber >= nextLBranchNumber) nextLBranchNumber = lNumber + 1
              }
              const newRow = branchName === 'M' ? 1 : row
              queue.push({ nodeId: childNodeId, row: newRow, col: col + 1, branchName: targetBranchName })
              console.log(`    🎬 分支结局 ${childNodeId} 归属 ${targetBranchName} 行(row=${newRow})`)
            }
          } else {
            // 检查子节点是否已经有明确的L系列归属（支持多级：L1-1, L2-1-1, L2-2-1等）
            // 匹配模式：L{n}-{m} 或 L{n}-{m}-{k}
            const lSeriesMatch = childNodeId.match(/^(L\d+)(?:-\d+)+$/)
            let targetBranchName = null
            
            if (lSeriesMatch) {
              // 子节点有L系列编号，提取顶级分支名（如 L1, L2, L3）
              const lSeriesName = lSeriesMatch[1] // 'L1', 'L2', 'L3'...
              
              // 查找是否已经有这个L系列分支
              if (branchNodes.has(lSeriesName)) {
                // 已存在，归属到现有分支
                targetBranchName = lSeriesName
                console.log(`    ↪️ 归属到现有分支 ${lSeriesName}: ${childNodeId}`)
              } else {
                // 不存在，创建新分支并用L系列名称
                targetBranchName = lSeriesName
                
                branchNodes.set(targetBranchName, [])
                branchParent.set(targetBranchName, branchName)
                if (!branchChildren.has(branchName)) {
                  branchChildren.set(branchName, [])
                }
                branchChildren.get(branchName).push(targetBranchName)
                branchAppearOrder.push(targetBranchName)
                
                // 更新L分支计数器
                const lNumber = parseInt(lSeriesName.replace('L', ''))
                if (lNumber >= nextLBranchNumber) {
                  nextLBranchNumber = lNumber + 1
                }
                
                console.log(`    🌿 创建新分支 ${targetBranchName} (基于节点ID, 父分支: ${branchName})`)
              }
            } else {
              // 子节点没有明确的L系列归属，创建新分支
              targetBranchName = `L${nextLBranchNumber}`
              nextLBranchNumber++

              branchNodes.set(targetBranchName, [])
              branchParent.set(targetBranchName, branchName)
              if (!branchChildren.has(branchName)) {
                branchChildren.set(branchName, [])
              }
              branchChildren.get(branchName).push(targetBranchName)
              branchAppearOrder.push(targetBranchName)
              
              console.log(`    🌿 创建新分支 ${targetBranchName} (父分支: ${branchName})`)
            }

            // 计算新节点的基准行号
            let newRow
            if (branchName === 'M') {
              newRow = 1 // 从M主线出来的所有分支初始都在row 1
            } else {
              // 从L分支出来的分支，在父分支基础上+1
              newRow = row + 1
            }

            queue.push({
              nodeId: childNodeId,
              row: newRow,
              col: col + 1,
              branchName: targetBranchName
            })
            console.log(`    → 节点 ${childNodeId} 加入分支 ${targetBranchName} (row=${newRow})`)
          }
        })
      }
    }

    console.log('\n📊 分支结构分析:')
    console.log('  分支出现顺序:', branchAppearOrder)
    for (const [branchName, nodes] of branchNodes.entries()) {
      const parent = branchParent.get(branchName)
      const children = branchChildren.get(branchName) || []
      console.log(`  ${branchName}: ${nodes.length}个节点, 父分支=${parent}, 子分支=[${children.join(', ')}]`)
    }

    // ========== 第二步：优先行规则 + 碰撞检测 ==========
    console.log('\n🔍 第二步：优先行规则 + 碰撞检测...')
    
    const branchOffsets = new Map() // branchName -> offset
    branchOffsets.set('M', 0) // M主线不移动

    // 顶层分支"最新优先行"规则：
    // 从 M 主线分出的顶层 L 分支，按出现顺序排列，越新的越靠上（row 1），
    // 之前的顶层分支依次整体下移一行。
    const topLevelBranches = (branchChildren.get('M') || []).filter(Boolean)
    if (topLevelBranches.length > 0) {
      topLevelBranches.forEach((branchName, index) => {
        const baseShift = topLevelBranches.length - 1 - index // 最新的index最大，shift=0
        branchOffsets.set(branchName, baseShift)
        if (baseShift > 0) {
          console.log(`  ⬇️ 顶层分支预偏移: ${branchName} 下移 ${baseShift} 行 (最新优先)`) 
        } else {
          console.log(`  ⬆️ 顶层分支保持row 1: ${branchName}`)
        }
      })
    }

    // 按分支出现顺序处理（从后往前）
    for (let i = branchAppearOrder.length - 1; i >= 0; i--) {
      const currentBranch = branchAppearOrder[i]
      if (currentBranch === 'M') continue // M主线不检测

      const currentNodes = branchNodes.get(currentBranch) || []
      const currentOffset = branchOffsets.get(currentBranch) || 0
      
      console.log(`\n  检查分支 ${currentBranch} (${currentNodes.length}个节点, 当前offset=${currentOffset})`)

      // 检查是否与之前的分支冲突
      for (let j = 0; j < i; j++) {
        const earlierBranch = branchAppearOrder[j]
        if (earlierBranch === 'M') continue

        const earlierNodes = branchNodes.get(earlierBranch) || []
        const earlierOffset = branchOffsets.get(earlierBranch) || 0

        // 检查是否有列重叠
        for (const currentNodeId of currentNodes) {
          const currentCol = nodeCols.get(currentNodeId)
          const currentBaseRow = nodeRows.get(currentNodeId)
          const currentFinalRow = currentBaseRow + currentOffset

          for (const earlierNodeId of earlierNodes) {
            const earlierCol = nodeCols.get(earlierNodeId)
            const earlierBaseRow = nodeRows.get(earlierNodeId)
            const earlierFinalRow = earlierBaseRow + earlierOffset

            // 同列且行号冲突
            if (currentCol === earlierCol && Math.abs(currentFinalRow - earlierFinalRow) < 1) {
              // 需要下移早期分支（及其所有子分支）
              const newOffset = currentFinalRow - earlierBaseRow + 1
              
              if (newOffset > earlierOffset) {
                branchOffsets.set(earlierBranch, newOffset)
                console.log(`    ⬇️ ${earlierBranch} 需要避让 ${currentBranch}`)
                console.log(`      冲突: ${currentNodeId}(row=${currentFinalRow}) vs ${earlierNodeId}(row=${earlierFinalRow})`)
                console.log(`      ${earlierBranch} offset: ${earlierOffset} → ${newOffset}`)

                // 递归下移该分支的所有子分支
                const shiftChildBranches = (parentBranch, additionalOffset) => {
                  const children = branchChildren.get(parentBranch) || []
                  for (const childBranch of children) {
                    const childCurrentOffset = branchOffsets.get(childBranch) || 0
                    const childNewOffset = childCurrentOffset + additionalOffset
                    branchOffsets.set(childBranch, childNewOffset)
                    console.log(`      ⬇️⬇️ 子分支 ${childBranch} 跟随下移: ${childCurrentOffset} → ${childNewOffset}`)
                    // 递归处理孙分支
                    shiftChildBranches(childBranch, additionalOffset)
                  }
                }

                shiftChildBranches(earlierBranch, newOffset - earlierOffset)
              }
            }
          }
        }
      }
    }

    // ========== 第三步：应用偏移量 ==========
    console.log('\n📐 第三步：应用偏移量...')
    for (const [nodeId, baseRow] of nodeRows.entries()) {
      const branchName = nodeBranch.get(nodeId)
      const offset = branchOffsets.get(branchName) || 0
      const finalRow = baseRow + offset
      nodeRows.set(nodeId, finalRow)
      console.log(`  ${nodeId}: baseRow=${baseRow} + offset=${offset} = finalRow=${finalRow} (分支=${branchName})`)
    }

    // ========== 第四步：转换为实际坐标 ==========
    storyStructure.nodes.forEach(node => {
      const row = nodeRows.get(node.id) || 0
      const col = nodeCols.get(node.id) || 0
      
      const x = baseX + col * horizontalSpacing
      const y = baseY + row * verticalSpacing
      
      nodePositions.set(node.id, { x, y })
    })

    console.log('✅ 布局计算完成')
    return nodePositions
  }

  // 将AI生成的故事转换为画布节点和边
  const convertAIStoryToCanvas = (aiStory) => {
    console.log('🎬 开始转换AI故事:', aiStory)
    console.log('📊 故事数据结构分析:', {
      title: aiStory.title,
      hasScenes: !!aiStory.scenes,
      scenesCount: aiStory.scenes?.length || 0,
      hasChapters: !!aiStory.chapters,
      chaptersCount: aiStory.chapters?.length || 0,
      hasCharacters: !!aiStory.characters,
      charactersCount: aiStory.characters?.length || 0
    })
    
    const nodes = []
    const edges = []
    let nodeIdCounter = 1
    
    // 创建开始节点
    const startNode = {
      id: `start-${nodeIdCounter++}`,
      type: 'storyNode',
      position: { x: 400, y: 100 },
      data: {
        title: aiStory.title || aiStory.outline?.title || '故事开始',
        description: aiStory.summary || aiStory.outline?.summary || 'AI生成的故事',
        type: 'start',
        content: aiStory.summary || aiStory.outline?.summary || '从前有一个故事...',
        imagePrompt: '',
        sceneImage: '',
        branches: [],
        dialogues: [],
        selectedCharacters: []
      }
    }
    nodes.push(startNode)
    
    // 按章节和场景顺序排序
    const sortedScenes = aiStory.scenes && aiStory.scenes.length > 0 
      ? [...aiStory.scenes].sort((a, b) => {
          if (a.chapter !== b.chapter) return a.chapter - b.chapter
          return a.scene - b.scene
        })
      : []
    
    console.log('🎭 场景数据详细分析:')
    sortedScenes.forEach((scene, index) => {
      const hasChoices = !!(scene.choices && scene.choices.length > 0)
      console.log(`场景 ${index + 1}: ${scene.title || scene.id}`, {
        id: scene.id,
        sceneId: scene.sceneId,
        hasChoices,
        choicesCount: scene.choices?.length || 0,
        choices: scene.choices?.map(c => ({
          text: c.text,
          targetNodeId: c.targetNodeId,
          nextScene: c.nextScene,
          id: c.id
        })) || [],
        content: scene.content?.substring(0, 50) + '...'
      })
    })
    
    // 检查是否有分支数据，如果没有则创建示例分支
    const totalChoices = sortedScenes.reduce((sum, scene) => sum + (scene.choices?.length || 0), 0)
    console.log('🔀 总分支选项数:', totalChoices)
    
    if (totalChoices === 0 && sortedScenes.length > 0) {
      console.warn('⚠️ 没有找到分支数据，创建示例分支...')
      
      // 为第一个场景创建示例分支
      if (sortedScenes[0]) {
        sortedScenes[0].choices = [
          {
            id: 'choice-1-1',
            text: '勇敢面对挑战',
            targetNodeId: sortedScenes[1]?.id || 'scene-1-2',
            path: 'brave'
          },
          {
            id: 'choice-1-2', 
            text: '谨慎观察情况',
            targetNodeId: sortedScenes[1]?.id || 'scene-1-2',
            path: 'cautious'
          }
        ]
        console.log('✅ 已为第一个场景添加示例分支:', sortedScenes[0].choices)
      }
    }
    
    // 分析故事结构，创建分支连接关系
    const storyStructure = sortedScenes.length > 0 ? analyzeStoryStructure(sortedScenes) : null
    
    console.log('🔍 故事结构分析结果:', {
      hasStoryStructure: !!storyStructure,
      nodesCount: storyStructure?.nodes?.length || 0,
      connectionsCount: storyStructure?.connections?.length || 0,
      connections: storyStructure?.connections,
      nodeIds: storyStructure?.nodes?.map(n => n.id) || []
    })
    
    // 如果有故事结构，添加开始节点到结构中
    if (storyStructure && storyStructure.nodes.length > 0) {
      const firstSceneNode = storyStructure.nodes[0]
      
      // 在故事结构前添加开始节点
      storyStructure.nodes.unshift({
        id: startNode.id,
        scene: {
          id: startNode.id,
          title: startNode.data.title,
          chapter: 0,
          scene: 0,
          choices: []
        },
        isBranch: false,
        choices: []
      })
      
      // 添加从开始节点到第一个场景的连接到故事结构中
      if (firstSceneNode) {
        storyStructure.connections.unshift({
          id: `edge-start-to-${firstSceneNode.id}`,
          from: startNode.id,
          to: firstSceneNode.id,
          type: 'linear',
          label: '开始',
          fromHandle: null,
          toHandle: null,
          path: 'start'
        })
        console.log(`✅ 添加开始节点连接到故事结构: ${startNode.id} -> ${firstSceneNode.id}`)
      }
    }
    
    if (storyStructure && storyStructure.nodes.length > 0) {
      // 🔧 规范化数据格式,确保与布局算法兼容
      const normalizedStructure = {
        nodes: storyStructure.nodes.map(nodeInfo => ({
          id: nodeInfo.id,
          type: nodeInfo.scene?.type || 'scene',
          data: {
            sceneId: nodeInfo.id,
            label: nodeInfo.scene?.title || nodeInfo.id,
            isEnding: nodeInfo.scene?.isEnding || nodeInfo.scene?.type === 'ending' || /^E\d+$/.test(nodeInfo.id)
          }
        })),
        connections: storyStructure.connections.map(conn => ({
          from: conn.from,
          to: conn.to,
          label: conn.label
        }))
      }
      
      console.log('📊 规范化后的数据结构:', {
        节点数: normalizedStructure.nodes.length,
        连接数: normalizedStructure.connections.length,
        节点ID列表: normalizedStructure.nodes.map(n => n.id),
        连接列表: normalizedStructure.connections.map(c => `${c.from} -> ${c.to}`)
      })
      
      // 使用基于连接关系的智能布局算法
      const nodePositions = calculateNodePositions(normalizedStructure)
      
      storyStructure.nodes.forEach((nodeInfo, index) => {
        const scene = nodeInfo.scene
        const nodeId = nodeInfo.id
        
        console.log(`创建节点 ${index}: ${nodeId} (${scene.title}) - 分支数: ${scene.choices?.length || 0}, isBranch: ${nodeInfo.isBranch}`)
        
        const sceneNode = {
          id: nodeId,
          type: 'storyNode',
          position: nodePositions.get(nodeId),
          data: {
            title: scene.title || `第${scene.chapter}章 场景${scene.scene}`,
            description: scene.description || '',
            type: scene.type || 'scene',
            content: scene.content || '',
            imagePrompt: scene.imagePrompt || '',
            sceneImage: scene.sceneImage || scene.backgroundImage || '',
            imageFixed: scene.imageFixed || false,
            imageSource: scene.imageSource || 'none',
            branches: scene.choices || [],
            // 确保分支标识正确设置
            hasBranches: !!(scene.choices && scene.choices.length > 0),
            dialogues: convertDialoguesToEditorFormat(scene.dialogues || [], aiStory.characters || []),
            selectedCharacters: scene.selectedCharacters || [],
            chapter: scene.chapter || 1,
            scene: scene.scene || 1,
            isBranch: nodeInfo.isBranch,
            // 保存AI生成的所有相关数据
            aiData: {
              originalScene: scene,
              characters: scene.characters || [],
              choices: scene.choices || [],
              background: scene.background || '',
              mood: scene.mood || '',
              timeOfDay: scene.timeOfDay || '',
              location: scene.location || ''
            }
          },
          // 为分支场景添加多个输出连接点
          sourcePosition: 'right',
          targetPosition: 'left'
        }
        nodes.push(sceneNode)
        console.log(`节点 ${nodeId} 位置:`, nodePositions.get(nodeId))
      })
      
      // 开始节点的连接已经在storyStructure.connections中了，不需要重复创建
      console.log('📌 开始节点连接已在故事结构中')
      
      // 创建所有连接，根据连接类型使用不同样式
      storyStructure.connections.forEach((conn) => {
        console.log(`创建连接: ${conn.from} -> ${conn.to}, 类型: ${conn.type}, 标签: ${conn.label}`)
        
        // 根据连接类型设置不同的样式
        let edgeStyle = { strokeWidth: 2 }
        let edgeType = 'default'
        let animated = false
        
        switch (conn.type) {
          case 'linear':
            edgeStyle = { stroke: '#10B981', strokeWidth: 6, zIndex: 1000 } // 绿色 - 线性连接
            edgeType = 'straight'
            animated = false
            break
          case 'branch':
            edgeStyle = { stroke: '#3B82F6', strokeWidth: 6, zIndex: 1000 } // 蓝色 - 分支连接
            edgeType = 'straight'
            animated = false
            break
          case 'convergence':
            edgeStyle = { stroke: '#F59E0B', strokeWidth: 6, zIndex: 1000 } // 橙色 - 汇聚连接
            edgeType = 'straight'
            animated = false
            break
          default:
            edgeStyle = { stroke: '#6B7280', strokeWidth: 6, zIndex: 1000 } // 灰色 - 默认连接
            edgeType = 'straight'
            animated = false
        }
        
        const edge = {
          id: conn.id,
          source: conn.from,
          sourceHandle: conn.fromHandle || null,
          target: conn.to,
          targetHandle: conn.toHandle || null,
          type: edgeType,
          animated: animated,
          style: edgeStyle,
          label: conn.label,
          labelStyle: { 
            fontSize: 12, 
            fontWeight: 'bold',
            fill: edgeStyle.stroke 
          },
          labelBgStyle: { 
            fill: 'rgba(255, 255, 255, 0.8)', 
            fillOpacity: 0.8,
            rx: 4,
            ry: 4
          },
          data: {
            connectionType: conn.type,
            path: conn.path,
            choice: conn.choice
          }
        }
        
        console.log(`🔗 创建边详情:`, {
          id: edge.id,
          source: edge.source,
          sourceHandle: edge.sourceHandle,
          target: edge.target,
          targetHandle: edge.targetHandle,
          label: edge.label
        })
        
        edges.push(edge)
      })
      
      console.log('创建的所有连接:', edges)
    }
    
    // 结局节点已经集成到分支路径中，不需要单独创建
    
    // 只在调试模式下显示详细日志
    // console.log('AI故事转换完成，生成节点:', nodes.length, '边:', edges.length)
    // console.log('节点详情:', nodes.map(n => ({ id: n.id, title: n.data.title, chapter: n.data.chapter, scene: n.data.scene })))
    // console.log('连接详情:', edges.map(e => ({ from: e.source, to: e.target, label: e.label })))
    
    // 验证转换结果 - 保留重要信息
    console.log('=== AI故事转换验证 ===')
    console.log('故事标题:', aiStory.title || aiStory.outline?.title)
    console.log('故事概要:', aiStory.summary || aiStory.outline?.summary)
    console.log('总场景数:', aiStory.scenes?.length)
    console.log('总角色数:', aiStory.characters?.length)
    console.log('分支选项数:', aiStory.scenes?.reduce((sum, s) => sum + (s.choices?.length || 0), 0))
    console.log('对话数量:', aiStory.scenes?.reduce((sum, s) => sum + (s.dialogues?.length || 0), 0))
    
    // 角色统计
    const characterStats = {}
    aiStory.scenes?.forEach(scene => {
      scene.dialogues?.forEach(dialogue => {
        const charName = dialogue.character
        if (!characterStats[charName]) {
          characterStats[charName] = { count: 0, scenes: new Set() }
        }
        characterStats[charName].count++
        characterStats[charName].scenes.add(scene.id)
      })
    })
    
    console.log('角色对话统计:', Object.entries(characterStats).map(([name, stats]) => ({
      name,
      dialogueCount: stats.count,
      sceneCount: stats.scenes.size
    })))
    
    console.log('场景详情:', aiStory.scenes?.map(s => ({
      id: s.id,
      title: s.title,
      choices: s.choices?.length || 0,
      dialogues: s.dialogues?.length || 0,
      characters: [...new Set(s.dialogues?.map(d => d.character) || [])],
      sceneImage: s.sceneImage ? '有图片' : '无图片'
    })))
    console.log('=======================')
    
    return { nodes, edges }
  }
  
  useEffect(() => {
    const story = stories.find(s => s.id === storyId)
    if (story && !initializedRef.current) {
      // console.log('首次初始化画布:', story.id)
      setCurrentStory(story)
      initializeCanvas(story).catch(error => {
        console.error('初始化画布时出错:', error)
      })
      initializedRef.current = true
    }
  }, [storyId, stories, setCurrentStory])

  // 监控画布状态变化
  useEffect(() => {
    if (initializedRef.current) {
      // 只在调试模式下显示日志
      console.log('画布状态变化 - 节点数量:', nodes.length, '边数量:', edges.length)
      console.log('当前节点列表:', nodes.map(n => ({ id: n.id, type: n.type, position: n.position })))
    }
  }, [nodes.length, edges.length])

  // 确保ReactFlow正确初始化
  useEffect(() => {
    if (reactFlowWrapper.current) {
      console.log('ReactFlow容器已准备就绪')
      // 确保容器有正确的高度
      const container = reactFlowWrapper.current
      if (container.offsetHeight === 0) {
        console.warn('ReactFlow容器高度为0，可能导致交互问题')
      }
    }
  }, [currentStory])

  // 恢复图片URL的函数
  const restoreImageUrls = async (nodes) => {
    if (!nodes || nodes.length === 0) return nodes
    
    const restoredNodes = await Promise.all(nodes.map(async (node) => {
      if (!node.data) return node
      
      const { sceneImage, backgroundImage, imageFileName } = node.data
      const imageUrl = sceneImage || backgroundImage
      
      if (imageUrl && imageUrl.startsWith('blob:')) {
        try {
          // 检查blob URL是否仍然有效
          const response = await fetch(imageUrl)
          if (!response.ok) {
            console.log(`🔄 图片URL已失效，尝试从IndexedDB恢复: ${imageUrl}`)
            
            // 优先使用保存的文件名，否则从URL中提取
            let fileName = imageFileName
            if (!fileName) {
              const urlParts = imageUrl.split('/')
              fileName = urlParts[urlParts.length - 1]
            }
            
            console.log(`🔍 尝试恢复图片: ${fileName}`)
            
            // 尝试从IndexedDB获取图片数据
            const restoredUrl = await localCloudService.getImageUrl(fileName)
            if (restoredUrl) {
              console.log(`✅ 图片URL已恢复: ${restoredUrl}`)
              return {
                ...node,
                data: {
                  ...node.data,
                  sceneImage: sceneImage ? restoredUrl : node.data.sceneImage,
                  backgroundImage: backgroundImage ? restoredUrl : node.data.backgroundImage
                }
              }
            } else {
              console.warn(`⚠️ 无法从IndexedDB恢复图片: ${fileName}`)
            }
          } else {
            console.log(`✅ 图片URL仍然有效: ${imageUrl}`)
          }
        } catch (error) {
          console.log(`🔄 图片URL已失效，尝试从IndexedDB恢复: ${imageUrl}`)
          
          // 优先使用保存的文件名，否则从URL中提取
          let fileName = imageFileName
          if (!fileName) {
            const urlParts = imageUrl.split('/')
            fileName = urlParts[urlParts.length - 1]
          }
          
          console.log(`🔍 尝试恢复图片: ${fileName}`)
          
          // 尝试从IndexedDB获取图片数据
          const restoredUrl = await localCloudService.getImageUrl(fileName)
          if (restoredUrl) {
            console.log(`✅ 图片URL已恢复: ${restoredUrl}`)
            return {
              ...node,
              data: {
                ...node.data,
                sceneImage: sceneImage ? restoredUrl : node.data.sceneImage,
                backgroundImage: backgroundImage ? restoredUrl : node.data.backgroundImage
              }
            }
          } else {
            console.warn(`⚠️ 无法从IndexedDB恢复图片: ${fileName}`)
          }
        }
      }
      
      return node
    }))
    
    return restoredNodes
  }

  // 初始化画布
  const initializeCanvas = async (story) => {
    // console.log('初始化画布:', story)
    
    // 检查是否有用户编辑过的数据（从IndexedDB读取）
    const userEditedKey = `user-edited-${story.id}`
    let userEditedData = null
    try {
      userEditedData = await localCloudService.loadFromIndexedDB(userEditedKey)
    } catch (e) {
      console.log('从IndexedDB读取用户数据失败，尝试localStorage')
      const storedData = localStorage.getItem(userEditedKey)
      if (storedData) userEditedData = JSON.parse(storedData)
    }
    
    // 尝试从IndexedDB读取AI生成的故事内容
    const aiStoryKey = `ai-story-${story.id}`
    let aiStoryData = null
    try {
      aiStoryData = await localCloudService.loadFromIndexedDB(aiStoryKey)
    } catch (e) {
      const storedData = localStorage.getItem(aiStoryKey)
      if (storedData) aiStoryData = JSON.parse(storedData)
    }
    
    console.log('🔍 检查故事数据状态:', {
      storyId: story.id,
      hasUserEdited: !!userEditedData,
      hasAiData: !!aiStoryData,
      hasExistingNodes: !!(story.nodes && story.nodes.length > 0)
    })
    
    // 如果用户已经编辑过，优先使用用户编辑的数据
    if (userEditedData) {
      console.log('✅ 发现用户编辑的数据，使用用户版本')
      try {
        // 恢复图片URL
        const restoredNodes = await restoreImageUrls(userEditedData.nodes || [])
        setNodes(restoredNodes)
        setEdges(userEditedData.edges || [])
        return
      } catch (error) {
        console.error('❌ 解析用户编辑数据失败:', error)
      }
    }
    
    // 添加详细的localStorage调试
    if (aiStoryData) {
      try {
        // 动态导入调试工具
        import('../utils/localStorageDebugger.js').then(({ LocalStorageDebugger }) => {
          console.log('🔧 执行localStorage完整调试...')
          LocalStorageDebugger.generateFullReport(story.id)
        }).catch(error => {
          console.log('⚠️ 调试工具导入失败:', error.message)
        })
      } catch (error) {
        console.log('⚠️ 调试工具执行失败:', error.message)
      }
    }
    
    if (aiStoryData) {
      console.log('✅ 发现AI生成的故事数据，正在转换为画布节点')
      try {
        // aiStoryData 从IndexedDB读取，已经是对象
        const aiStory = typeof aiStoryData === 'string' ? JSON.parse(aiStoryData) : aiStoryData
        console.log('📖 AI故事数据结构:', {
          title: aiStory.title,
          summary: aiStory.summary,
          scenesCount: aiStory.scenes?.length,
          charactersCount: aiStory.characters?.length,
          hasOutline: !!aiStory.outline
        })
        
        // 详细的AI数据调试
        console.log('🔍 详细AI数据调试:')
        console.log('完整的aiStory对象:', aiStory)
        console.log('aiStory.outline:', aiStory.outline)
        console.log('aiStory.title:', aiStory.title)
        console.log('aiStory.summary:', aiStory.summary)
        console.log('aiStory.characters:', aiStory.characters)
        console.log('aiStory.chapters:', aiStory.chapters)
        console.log('aiStory.scenes:', aiStory.scenes)
        
        // 检查是否有嵌套的数据
        if (aiStory.outline) {
          console.log('📋 outline详情:')
          console.log('  outline.title:', aiStory.outline.title)
          console.log('  outline.summary:', aiStory.outline.summary)
          console.log('  outline.mainPlot:', aiStory.outline.mainPlot)
        }
        
        // 检查字符详情
        if (aiStory.characters && aiStory.characters.length > 0) {
          console.log('👥 角色详情:')
          aiStory.characters.forEach((char, index) => {
            console.log(`  角色${index + 1}:`, {
              name: char.name,
              role: char.role,
              description: char.description
            })
          })
        }
        
        // 检查章节详情并扁平化场景数据
        let allScenes = []
        const originalScenes = aiStory.scenes // 保存原始scenes数组
        
        // 优先使用storyboard数据（多分支多结局）
        if (aiStory.storyboard && aiStory.storyboard.shots && aiStory.storyboard.shots.length > 0) {
          console.log('🎬 发现storyboard数据，使用分镜shots数组')
          console.log('📊 storyboard.shots数组长度:', aiStory.storyboard.shots.length)
          allScenes = aiStory.storyboard.shots.map((shot, index) => ({
            ...shot,
            chapter: shot.chapter || 1,
            scene: shot.scene || shot.shotNumber || index + 1,
            id: shot.id || shot.sceneId || `scene-${shot.shotNumber || index + 1}`,
            // 确保有choices数据用于分支
            choices: shot.choices || [],
            // 确保有branches数据用于兼容
            branches: shot.branches || []
          }))
          console.log(`📊 从storyboard转换的场景数: ${allScenes.length}`)
          console.log('\n🔍 详细场景数据（包含choices）:')
          
          // 创建场景ID映射
          const sceneIdSet = new Set(allScenes.map(s => s.id))
          console.log('📋 可用的场景ID列表:', Array.from(sceneIdSet))
          
          let missingTargets = []
          allScenes.forEach((scene, idx) => {
            if (scene.choices && scene.choices.length > 0) {
              console.log(`  场景 ${idx + 1} (${scene.id}): ${scene.title}`)
              scene.choices.forEach((choice, choiceIdx) => {
                const targetId = choice.targetNodeId || choice.targetSceneId || choice.nextScene
                const targetExists = targetId && sceneIdSet.has(targetId)
                
                console.log(`    选择 ${choiceIdx + 1}:`, {
                  text: choice.text,
                  targetNodeId: choice.targetNodeId || 'null',
                  targetSceneId: choice.targetSceneId || 'null',
                  nextScene: choice.nextScene || 'null',
                  finalTarget: targetId || '❌ 未设置',
                  targetExists: targetExists ? '✅' : '❌'
                })
                
                if (!targetId) {
                  console.error(`    ❌ 错误: 选项 "${choice.text}" 没有任何目标ID字段！`)
                  missingTargets.push({
                    sceneId: scene.id,
                    sceneTitle: scene.title,
                    choiceText: choice.text,
                    issue: '缺少targetNodeId'
                  })
                } else if (!targetExists) {
                  console.error(`    ❌ 错误: 目标场景 "${targetId}" 不存在！`)
                  missingTargets.push({
                    sceneId: scene.id,
                    sceneTitle: scene.title,
                    choiceText: choice.text,
                    targetId: targetId,
                    issue: '目标场景不存在'
                  })
                }
              })
            }
          })
          
          if (missingTargets.length > 0) {
            console.error('\n❌ 发现 ' + missingTargets.length + ' 个连接问题:')
            missingTargets.forEach((issue, idx) => {
              console.error(`  ${idx + 1}. ${issue.sceneId} (${issue.sceneTitle})`)
              console.error(`     选项: "${issue.choiceText}"`)
              console.error(`     问题: ${issue.issue}`)
              if (issue.targetId) {
                console.error(`     目标: ${issue.targetId}`)
              }
            })
            console.error('\n💡 这些问题将导致节点无法连接，请检查AI生成的数据')
          } else {
            console.log('\n✅ 所有choices连接验证通过！')
          }
        }
        
        // 只有在没有storyboard数据时才处理章节数据
        if (allScenes.length === 0 && aiStory.chapters && aiStory.chapters.length > 0) {
          console.log('📚 章节详情:')
          aiStory.chapters.forEach((chapter, index) => {
            console.log(`  章节${index + 1}:`, {
              title: chapter.title,
              description: chapter.description,
              scenesCount: chapter.scenes?.length || 0
            })
            
            // 将章节中的场景扁平化到总场景数组中
            if (chapter.scenes && Array.isArray(chapter.scenes)) {
              chapter.scenes.forEach((scene, sceneIndex) => {
                // 确保场景包含章节信息和场景编号
                const enrichedScene = {
                  ...scene,
                  chapter: chapter.id || index + 1,
                  scene: scene.scene || sceneIndex + 1, // 确保有场景编号
                  chapterTitle: chapter.title,
                  // 确保场景有唯一ID
                  id: scene.id || `scene-${chapter.id || index + 1}-${scene.scene || sceneIndex + 1}`
                }
                allScenes.push(enrichedScene)
                console.log(`    添加镜头: ${scene.title} (${enrichedScene.id}) - 对话数: ${scene.dialogues?.length || 0}`)
                
                // 详细调试场景的所有字段
                console.log(`      场景完整数据:`, {
                  id: scene.id,
                  title: scene.title,
                  description: scene.description,
                  content: scene.content?.substring(0, 100) + '...',
                  hasDialogues: !!scene.dialogues,
                  dialoguesType: typeof scene.dialogues,
                  dialoguesLength: scene.dialogues?.length,
                  dialoguesRaw: scene.dialogues,
                  hasChoices: !!scene.choices,
                  choicesLength: scene.choices?.length,
                  choicesData: scene.choices,
                  hasChoicesField: 'choices' in scene,
                  allKeys: Object.keys(scene)
                })
                
                // 详细调试对话信息
                if (scene.dialogues && scene.dialogues.length > 0) {
                  console.log(`      对话预览:`, scene.dialogues.slice(0, 2).map(d => ({
                    character: d.character,
                    content: d.content?.substring(0, 50) + '...'
                  })))
                } else {
                  console.log(`      ❌ 该镜头没有对话数据，将基于镜头内容生成简单对话`)
                  
                  // 基于镜头内容生成简单的对话
                  if (scene.content) {
                    const generatedDialogues = generateFallbackDialogues(scene, aiStory.characters || []).map(d => ({
                      ...d,
                      emotion: 'neutral'
                    }))
                    enrichedScene.dialogues = generatedDialogues
                    console.log(`      ✅ 生成了 ${generatedDialogues.length} 条后备对话`)
                  }
                }
              })
            }
          })
          console.log(`📊 总扁平化场景数: ${allScenes.length}`)
        }
        
        // 如果没有从章节中找到场景，使用原始的scenes数组
        if (allScenes.length === 0 && originalScenes && Array.isArray(originalScenes) && originalScenes.length > 0) {
          console.log('⚠️ 没有找到章节中的场景，使用直接的scenes数组')
          console.log('📊 原始scenes数组长度:', originalScenes.length)
          allScenes = originalScenes.map((scene, index) => ({
            ...scene,
            chapter: scene.chapter || 1,
            scene: scene.scene || scene.shotNumber || index + 1,
            id: scene.id || scene.sceneId || `scene-${scene.shotNumber || index + 1}`
          }))
          console.log(`📊 处理后场景数: ${allScenes.length}`)
        }
        
        // 更新aiStory的scenes
        aiStory.scenes = allScenes
        console.log(`✅ 最终场景数: ${aiStory.scenes.length}`)
        
        const { nodes: aiNodes, edges: aiEdges } = convertAIStoryToCanvas(aiStory)
        
        // console.log('AI故事转换完成:', { nodes: aiNodes.length, edges: aiEdges.length })
        setNodes(aiNodes)
        setEdges(aiEdges)
        
                // 保存转换后的节点和边到故事中，同时同步角色和章节信息
        setTimeout(async () => {
          // 处理角色信息，确保所有角色都有完整信息
          const processedCharacters = (aiStory.characters || []).map(char => {
            // 转换AI角色类型到编辑器期望的格式
            let editorRole = 'supporting' // 默认为配角
            if (char.role === 'protagonist') {
              editorRole = 'main'
            } else if (char.role === 'supporting') {
              editorRole = 'supporting'
            } else if (char.role === 'antagonist') {
              editorRole = 'npc'
            }
            
            return {
              ...char,
              // 确保角色有完整的属性
              role: editorRole, // 使用转换后的角色类型
              languageStyle: char.languageStyle || '普通',
              isActive: char.isActive !== undefined ? char.isActive : true,
              firstAppearance: char.firstAppearance || '',
              lastAppearance: char.lastAppearance || '',
              dialogueCount: char.dialogueCount || 0,
              // 确保角色有必需的字段
              appearance: char.appearance || char.description || '暂无描述',
              background: char.background || '暂无背景',
              personality: char.personality || '暂无性格描述',
              imagePrompt: char.imagePrompt || '',
              avatar: char.avatar || '',
              // 确保角色有正确的ID格式
              id: char.id || `char-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
            }
          })
          
          // 为场景生成图片（智能选择真实API或模拟图片）
          // 但保留用户已上传的本地图片
          const imageConfig = settingsService.getImageGenerationConfig()
          const hasImageAPI = imageConfig.apiKey && imageConfig.apiUrl
          
          console.log('🖼️ 开始为场景生成图片...', { hasImageAPI, scenesCount: allScenes.length })
          
          const scenesWithImages = await Promise.all(allScenes.map(async (scene, index) => {
            // 使用ImageManager检查是否应该跳过图片生成
            if (ImageManager.shouldSkipImageGeneration(scene)) {
              console.log(`✅ 场景 ${index + 1} 保留固定图片 (${scene.imageSource || 'unknown'})`)
              return scene
            }
            
            if (scene.imagePrompt) {
              try {
                if (hasImageAPI) {
                  // 使用真实AI图像生成
                  const { RealAIService } = await import('../services/realAiService.js')
                  const imageUrl = await RealAIService.generateSceneImage(
                    scene.imagePrompt,
                    processedCharacters,
                    aiStory.style || '现代'
                  )
                  // 使用ImageManager设置固定图片
                  const updatedScene = ImageManager.setFixedImage(scene, imageUrl, 'ai')
                  Object.assign(scene, updatedScene)
                  console.log(`✅ 场景 ${index + 1} AI图片生成完成并固定`)
                } else {
                  // 使用模拟图片
                  const mockImageUrl = `https://picsum.photos/400/300?random=${Date.now() + index}`
                  const updatedScene = ImageManager.setFixedImage(scene, mockImageUrl, 'system')
                  Object.assign(scene, updatedScene)
                  console.log(`✅ 场景 ${index + 1} 模拟图片生成完成并固定`)
                }
              } catch (error) {
                console.error(`❌ 场景 ${index + 1} 图片生成失败:`, error)
                // 生成失败时使用模拟图片作为后备
                const fallbackImageUrl = `https://picsum.photos/400/300?random=${Date.now() + index}`
                const updatedScene = ImageManager.setFixedImage(scene, fallbackImageUrl, 'system')
                Object.assign(scene, updatedScene)
              }
            }
            return scene
          }))
          
          // 更新节点数据中的图片信息，使用ImageManager处理图片状态
          const updatedNodes = aiNodes.map(node => {
            const scene = scenesWithImages.find(s => s.id === node.id.replace('scene-', 'scene-'))
            if (scene && scene.sceneImage) {
              // 使用ImageManager更新节点图片信息
              const updatedNode = ImageManager.updateNodeImage(node, scene.sceneImage, scene.imageSource || 'ai')
              console.log(`✅ 节点 ${node.id} 图片已更新 (${scene.imageSource || 'unknown'})`)
              return updatedNode
            }
            return node
          })
          
          const updatedStoryData = { 
            nodes: updatedNodes, 
            edges: aiEdges,
            // 同步AI生成的角色和章节信息
            characters: processedCharacters,
            chapters: aiStory.chapters || [],
            title: aiStory.title || story.title,
            summary: aiStory.summary || story.description,
            outline: aiStory.outline || {}
          }
          
          updateStory(story.id, updatedStoryData)
          
          // 强制更新当前故事状态
          setCurrentStory({ ...story, ...updatedStoryData })
          
          // 更新画布节点状态以显示新图片
          setNodes(updatedNodes)
          
          console.log('✅ AI故事已保存到画布，覆盖了现有数据')
          console.log('👥 原始AI角色数据:', aiStory.characters)
          console.log('👥 处理后的角色数据:', processedCharacters)
          console.log('👥 角色信息已同步:', processedCharacters.map(c => ({ 
            id: c.id,
            name: c.name, 
            role: c.role, 
            originalRole: aiStory.characters?.find(ac => ac.name === c.name)?.role,
            languageStyle: c.languageStyle 
          })))
          console.log('🔍 当前故事角色数量:', processedCharacters.length)
          console.log('🔍 更新后的故事数据:', updatedStoryData)
        }, 100)
        
        return
      } catch (error) {
        console.error('❌ 转换AI故事失败:', error)
      }
    }
    
    if (!story.nodes || story.nodes.length === 0) {
      // 创建默认开始节点
      const defaultNodes = [
        {
          id: 'start-1',
          type: 'storyNode',
          position: { x: 400, y: 200 },
          data: {
            title: '故事开始',
            description: '这是你的故事开始的地方',
            type: 'start',
            content: '从前有一个勇敢的小战士...',
            imagePrompt: '',
            sceneImage: '',
            branches: [],
            dialogues: [],
            selectedCharacters: []
          }
        }
      ]
      setNodes(defaultNodes)
      setEdges([])
      // 保存默认数据
      setTimeout(() => saveCanvasData(), 100)
    } else {
      console.log('加载现有节点:', story.nodes)
      console.log('加载现有边:', story.edges)
      
      // 确保数据是深拷贝并验证修复
      const loadedNodes = story.nodes ? JSON.parse(JSON.stringify(story.nodes)) : []
      const loadedEdges = story.edges ? JSON.parse(JSON.stringify(story.edges)) : []
      
      // 验证和修复所有节点
      const validatedNodes = loadedNodes.map(node => validateAndFixNode(node)).filter(Boolean)
      
      console.log('节点验证结果:', {
        原始节点数: loadedNodes.length,
        验证后节点数: validatedNodes.length,
        修复的节点: validatedNodes.filter((node, index) => {
          const original = loadedNodes[index]
          return !original || 
                 !original.position || 
                 isNaN(original.position.x) || 
                 isNaN(original.position.y)
        }).length
      })
      
      // 恢复图片URL
      const restoredNodes = await restoreImageUrls(validatedNodes)
      setNodes(restoredNodes)
      setEdges(loadedEdges)
      
              // console.log('画布初始化完成，节点数量:', loadedNodes.length, '边数量:', loadedEdges.length)
    }
  }

  // 防抖保存函数
  const debouncedSave = useRef(null)
  
  // 存储清理工具函数
  const cleanupOldStorage = () => {
    try {
      console.log('🧹 开始清理localStorage存储空间...')
      let cleanedSize = 0
      let cleanedCount = 0
      
      // 获取当前存储使用情况
      const currentUsage = JSON.stringify(localStorage).length
      const maxSize = 3 * 1024 * 1024 // 3MB
      const usagePercent = (currentUsage / maxSize) * 100
      
      console.log(`当前使用率: ${usagePercent.toFixed(1)}%，开始激进清理...`)
      
      // 清理可能损坏的存储数据
      const keys = Object.keys(localStorage)
      
      // 如果使用率超过100%，进行紧急清理
      if (usagePercent > 100) {
        console.warn('🚨 存储空间严重超限，执行紧急清理...')
        
        // 1. 清理所有临时和缓存数据
        keys.forEach(key => {
          const originalSize = localStorage.getItem(key)?.length || 0
          
          if (key.startsWith('temp-') || 
              key.startsWith('cache-') || 
              key.startsWith('guided-edit-') ||
              key.startsWith('chat-history-') ||
              key.startsWith('ai-response-') ||
              key.includes('backup-') ||
              key.includes('draft-')) {
            console.log(`紧急清理: ${key} (${(originalSize/1024).toFixed(2)}KB)`)
            localStorage.removeItem(key)
            cleanedSize += originalSize
            cleanedCount++
          }
        })
        
        // 2. 清理过大的数据项（超过100KB）
        keys.forEach(key => {
          if (!localStorage.getItem(key)) return // 已被删除
          
          const originalSize = localStorage.getItem(key)?.length || 0
          if (originalSize > 100 * 1024) {
            console.warn(`清理过大数据项: ${key} (${(originalSize/1024).toFixed(2)}KB)`)
            localStorage.removeItem(key)
            cleanedSize += originalSize
            cleanedCount++
          }
        })
      }
      
      // 常规清理逻辑
      keys.forEach(key => {
        if (!localStorage.getItem(key)) return // 已被删除
        
        const originalSize = localStorage.getItem(key)?.length || 0
        
        // 清理损坏的数据
        if (key.startsWith('altverse-') || key.startsWith('user-edited-')) {
          try {
            const data = JSON.parse(localStorage.getItem(key))
            // 如果数据包含循环引用或无效值，删除它
            if (JSON.stringify(data).includes('NaN') || 
                JSON.stringify(data).includes('undefined') ||
                JSON.stringify(data).includes('[object Object]')) {
              console.warn(`清理损坏的存储数据: ${key}`)
              localStorage.removeItem(key)
              cleanedSize += originalSize
              cleanedCount++
            }
          } catch (error) {
            console.warn(`清理无效的存储数据: ${key}`, error)
            localStorage.removeItem(key)
            cleanedSize += originalSize
            cleanedCount++
          }
        }
        
        // 清理过期的临时数据（缩短到6小时）
        if (key.startsWith('temp-') || key.startsWith('cache-') || key.startsWith('guided-edit-')) {
          try {
            const data = JSON.parse(localStorage.getItem(key))
            const now = Date.now()
            const dataAge = now - (data.timestamp || data.createdAt || 0)
            
            // 如果存储紧张，清理超过6小时的临时数据
            const maxAge = usagePercent > 80 ? 6 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000
            if (dataAge > maxAge) {
              console.log(`清理过期临时数据: ${key}`)
              localStorage.removeItem(key)
              cleanedSize += originalSize
              cleanedCount++
            }
          } catch (error) {
            // 如果解析失败，直接删除
            localStorage.removeItem(key)
            cleanedSize += originalSize
            cleanedCount++
          }
        }
      })
      
      console.log(`✅ 清理完成: 删除${cleanedCount}项，释放${(cleanedSize/1024).toFixed(2)}KB空间`)
      
      // 清理后重新检查空间
      const newUsage = checkStorageSpace()
      console.log(`📊 清理后localStorage使用率: ${newUsage.usagePercent.toFixed(1)}%`)
      
    } catch (error) {
      console.error('清理存储时出错:', error)
    }
  }

  // 重置画布数据
  const resetCanvasData = () => {
    try {
      console.log('重置画布数据...')
      
      // 创建默认的开始节点
      const defaultNodes = [
        {
          id: 'start-1',
          type: 'storyNode',
          position: { x: 400, y: 200 },
          data: {
            title: '故事开始',
            description: '这是你的故事开始的地方',
            type: 'start',
            content: '从前有一个勇敢的小战士...',
            imagePrompt: '',
            sceneImage: '',
            imageFixed: false,
            imageSource: 'none',
            branches: [],
            dialogues: [],
            selectedCharacters: []
          }
        }
      ]
      
      setNodes(defaultNodes)
      setEdges([])
      
      // 保存重置后的数据
      if (currentStory) {
        updateStory(currentStory.id, {
          nodes: defaultNodes,
          edges: []
        })
      }
      
      console.log('画布数据已重置')
      toast.success('画布数据已重置')
    } catch (error) {
      console.error('重置画布数据时出错:', error)
      toast.error('重置画布数据失败')
    }
  }

  // ❌ 移除localStorage清理 - 不再使用localStorage
  // 数据现在完全存储在 sessionStorage + IndexedDB
  useEffect(() => {
    console.log('💡 跳过localStorage清理（已弃用）')
  }, [])

  // ❌ 已彻底移除localStorage检查 - 防止阻塞跳转
  // 现在完全使用 sessionStorage + IndexedDB
  const checkStorageSpace = () => {
    // 返回虚拟数据，不再检查localStorage
    return { used: 0, maxSize: 1, usagePercent: 0 }
  }

  // 紧急清理函数 - 当存储严重超限时使用
  const emergencyCleanup = () => {
    try {
      console.error('🚨 执行紧急存储清理...')
      const keys = Object.keys(localStorage)
      let cleanedSize = 0
      let cleanedCount = 0
      
      // 清理所有非核心数据
      keys.forEach(key => {
        const originalSize = localStorage.getItem(key)?.length || 0
        
        // 保留核心数据，清理其他所有数据
        if (!key.startsWith('altverse-stories') && 
            !key.startsWith('altverse-user') &&
            !key.startsWith('altverse-settings')) {
          console.log(`紧急删除: ${key} (${(originalSize/1024).toFixed(2)}KB)`)
          localStorage.removeItem(key)
          cleanedSize += originalSize
          cleanedCount++
        }
      })
      
      console.log(`🚨 紧急清理完成: 删除${cleanedCount}项，释放${(cleanedSize/1024).toFixed(2)}KB空间`)
      return { cleanedCount, cleanedSize }
    } catch (error) {
      console.error('紧急清理失败:', error)
      return { cleanedCount: 0, cleanedSize: 0 }
    }
  }

  // ✅ 存储策略：sessionStorage(临时) + IndexedDB(永久)
  // 完全不使用localStorage，避免容量限制导致跳转失败
  useEffect(() => {
    console.log('✅ 存储策略: sessionStorage(临时5MB+) + IndexedDB(永久200MB+)')
    console.log('✅ localStorage已彻底弃用，不再检查和清理')
  }, [])
  
  // 自动保存功能 - 优化版本
  useEffect(() => {
    const autoSaveInterval = setInterval(async () => {
      if (currentStory && (nodes.length > 0 || edges.length > 0)) {
        try {
          // 保存完整的用户编辑数据，不进行任何内容限制
          const userEditedData = {
            nodes: [...nodes],
            edges: [...edges],
            lastEdited: new Date().toISOString()
          }
          
          // 检查存储空间
          const dataString = JSON.stringify(userEditedData)
          const dataSize = new Blob([dataString]).size
          const storageKey = `user-edited-${currentStory.id}`
          
          console.log(`📊 自动保存数据大小: ${(dataSize / 1024).toFixed(2)}KB`)
          
          // 使用 storyStore 的 updateStory 方法（已整合IndexedDB）
          try {
            await updateStory(currentStory.id, {
              nodes: userEditedData.nodes,
              edges: userEditedData.edges,
              updatedAt: userEditedData.lastEdited
            })
            console.log('🔄 自动保存完成（IndexedDB）')
          } catch (error) {
            console.error('❌ 自动保存失败:', error)
          }
        } catch (error) {
          console.error('❌ 自动保存失败:', error)
        }
      }
    }, 60000) // 改为每60秒自动保存一次，减少频率
    
    return () => clearInterval(autoSaveInterval)
  }, [currentStory, nodes, edges])
  
  // 媒资选择处理函数
  const handleMediaSelect = (mediaItem) => {
    if (selectedNode && mediaItem) {
      const updatedNodes = nodes.map(node => 
        node.id === selectedNode.id 
          ? { 
              ...node, 
              data: { 
                ...node.data, 
                sceneImage: mediaItem.url,
                imageFileName: mediaItem.id
              } 
            }
          : node
      )
      setNodes(updatedNodes)
      toast.success('图片已更新！')
    }
  }

  // 数据恢复处理函数
  const handleDataRecovery = (recoveredData) => {
    if (recoveredData && recoveredData.nodes) {
      setNodes(recoveredData.nodes)
      setEdges(recoveredData.edges || [])
      toast.success('数据恢复成功！')
      console.log('✅ 数据已恢复:', recoveredData)
    }
  }

  // 回收站恢复处理函数
  const handleRecycleBinRestore = (restoredItem) => {
    if (restoredItem && restoredItem.type === 'story') {
      const storyData = restoredItem.data
      if (storyData.nodes) {
        setNodes(storyData.nodes)
        setEdges(storyData.edges || [])
        toast.success('故事已从回收站恢复！')
      }
    }
  }

  // 导入数据处理函数
  const handleImportData = (importResult) => {
    if (importResult.success) {
      toast.success(`导入成功: ${importResult.imported} 个项目`)
      if (currentStory) {
        initializeCanvas(currentStory)
      }
    }
  }

  // 拖拽优化函数
  const onNodeDragStart = useCallback((event, node) => {
    // 关闭右键菜单
    setCanvasContextMenu({ show: false, position: { x: 0, y: 0 }, flowPosition: { x: 0, y: 0 } })
    // 拖拽开始时减少重绘
    setNodes((nds) => 
      nds.map((n) => 
        n.id === node.id ? { ...n, dragging: true } : n
      )
    )
  }, [])

  const onNodeDrag = useCallback((event, node) => {
    // 拖拽过程中优化更新，减少状态更新频率
    if (node.dragging) {
      const validPosition = validateAndFixNodePosition(node.position)
      setNodes((nds) => 
        nds.map((n) => 
          n.id === node.id ? { ...n, position: validPosition } : n
        )
      )
    }
  }, [])

  const onNodeDragStop = useCallback((event, node) => {
    // 拖拽结束时恢复状态并保存
    setNodes((nds) => 
      nds.map((n) => 
        n.id === node.id ? { ...n, dragging: false } : n
      )
    )
    
    // 延迟保存到本地存储，避免频繁IO
    if (debouncedSave.current) {
      clearTimeout(debouncedSave.current)
    }
    
    debouncedSave.current = setTimeout(() => {
      saveCanvasData()
      debouncedSave.current = null
    }, 500)
  }, [])
  
  // 处理节点变化 - 性能优化版本
  const onNodesChangeCallback = useCallback(
    (changes) => {
      onNodesChange(changes)
      
      // 只在节点位置变化时保存，使用防抖优化
      const hasPositionChange = changes.some(change => change.type === 'position')
      if (hasPositionChange) {
        // 清除之前的定时器
        if (debouncedSave.current) {
          clearTimeout(debouncedSave.current)
        }
        
        // 设置新的防抖保存
        debouncedSave.current = setTimeout(() => {
          saveCanvasData()
          debouncedSave.current = null
        }, 300) // 增加防抖时间到300ms
      }
    },
    [onNodesChange]
  )

  // 处理边变化 - 性能优化版本
  const onEdgesChangeCallback = useCallback(
    (changes) => {
      onEdgesChange(changes)
      
      // 只在边被删除时保存，使用防抖优化
      const hasRemoval = changes.some(change => change.type === 'remove')
      if (hasRemoval) {
        // 清除之前的定时器
        if (debouncedSave.current) {
          clearTimeout(debouncedSave.current)
        }
        
        // 设置新的防抖保存
        debouncedSave.current = setTimeout(() => {
          saveCanvasData()
          debouncedSave.current = null
        }, 300)
      }
    },
    [onEdgesChange]
  )

  // 处理连接
  const onConnect = useCallback(
    (params) => {
      console.log('连接参数:', params)
      
      // 关闭右键菜单
      setCanvasContextMenu({ show: false, position: { x: 0, y: 0 }, flowPosition: { x: 0, y: 0 } })
      
      // 检查分支连接限制：每个分支只能连接一个场景
      if (params.sourceHandle && params.sourceHandle.startsWith('branch-right-')) {
        const branchId = params.sourceHandle.replace('branch-right-', '')
        const existingEdge = edges.find(edge => 
          edge.sourceHandle === params.sourceHandle
        )
        
        if (existingEdge) {
          toast.error('该分支已经连接到其他场景，请先断开现有连接')
          return
        }
      }
      
      const newEdge = {
        id: `edge-${Date.now()}`,
        source: params.source,
        target: params.target,
        sourceHandle: params.sourceHandle,
        targetHandle: params.targetHandle,
        type: 'straight', // 使用直线连接
        animated: false, // 不显示动画
        style: { 
          stroke: '#3B82F6', 
          strokeWidth: 6,
          zIndex: 1000
        },
        label: '', // 添加标签字段
        data: {
          connectionType: 'manual', // 标记为手动连接
          createdAt: new Date().toISOString()
        }
      }
      
      // 使用函数式更新边的状态
      setEdges(eds => {
        const updatedEdges = [...eds, newEdge]
        // 立即保存新的连接
        setTimeout(() => {
          if (currentStory) {
            updateStory(currentStory.id, { 
              nodes: nodes, 
              edges: updatedEdges 
            })
            toast.success(`连接成功: ${params.source} -> ${params.target}`)
            console.log('连接已保存到故事数据:', updatedEdges)
          }
        }, 50)
        return updatedEdges
      })
    },
    [setEdges, currentStory, nodes, edges, updateStory]
  )
  
  // 断开连接
  const disconnectEdge = (edgeId) => {
    setEdges(eds => {
      const updatedEdges = eds.filter(edge => edge.id !== edgeId)
      // 立即保存断开后的状态
      setTimeout(() => {
        if (currentStory) {
          updateStory(currentStory.id, { 
            nodes: nodes, 
            edges: updatedEdges 
          })
          
              // 同时保存用户编辑的数据到sessionStorage（不再使用localStorage）
              try {
                const userEditedData = {
                  nodes: nodes,
                  edges: updatedEdges,
                  lastEdited: new Date().toISOString()
                }
                sessionStorage.setItem(`user-edited-${currentStory.id}`, JSON.stringify(userEditedData))
              } catch (quotaError) {
                console.warn('⚠️ sessionStorage保存失败:', quotaError)
              }
          
          toast.success('连接已断开')
          console.log('连接断开，已保存到故事数据:', updatedEdges)
        }
      }, 50)
      return updatedEdges
    })
  }

  // 保存画布数据
  const saveCanvasData = async () => {
    if (currentStory) {
      // 直接更新当前故事的数据
      await updateStory(currentStory.id, { 
        nodes: [...nodes], 
        edges: [...edges] 
      })
      
      // 保存用户编辑数据到IndexedDB
      try {
        const userEditedData = {
          nodes: [...nodes],
          edges: [...edges],
          lastEdited: new Date().toISOString()
        }
        await localCloudService.saveToIndexedDB(`user-edited-${currentStory.id}`, userEditedData)
        console.log('✅ 画布数据已保存到IndexedDB')
      } catch (error) {
        console.warn('⚠️ 保存用户编辑数据失败:', error)
      }
    }
  }

  // 转换编辑器数据为智能引导格式
  const convertEditorDataToGuidedFormat = () => {
    if (!currentStory || !nodes || nodes.length === 0) {
      console.warn('⚠️ 没有可转换的数据')
      return null
    }

    // 从节点数据中提取分镜信息（完整字段）
    const shots = nodes.map((node, index) => {
      // 从节点ID或顺序获取镜头号
      const shotNumber = node.data.shotNumber || parseInt(node.id.split('-')[1]) || index + 1
      
      return {
        shotNumber: shotNumber,
        chapter: node.data.chapter || 1,
        scene: node.data.scene || shotNumber,
        sceneId: node.data.sceneId || node.id,
        sceneName: node.data.sceneName || node.data.title || `场景${shotNumber}`,
        title: node.data.title || `镜头${shotNumber}`,
        description: node.data.description || '',
        content: node.data.content || node.data.description || '',
        type: node.data.type || 'normal',
        
        // 视觉元素（完整对应）
        imagePrompt: node.data.imagePrompt || '',
        sceneImage: node.data.sceneImage || '',
        backgroundImage: node.data.backgroundImage || '',
        generatedImage: node.data.generatedImage || node.data.sceneImage || '',
        imageFixed: node.data.imageFixed || false,
        imageSource: node.data.imageSource || 'none',
        
        // 镜头参数
        cameraAngle: node.data.cameraAngle || '中景',
        duration: node.data.duration || '5秒',
        
        // 环境设定
        location: node.data.location || '',
        timeOfDay: node.data.timeOfDay || '',
        mood: node.data.mood || '',
        atmosphere: node.data.atmosphere || node.data.mood || '',
        background: node.data.background || '',
        musicStyle: node.data.musicStyle || '',
        
        // 角色和对话（完整对应）
        characters: node.data.characters || [],
        selectedCharacters: node.data.selectedCharacters || [],
        dialogues: (node.data.dialogues || []).map(d => ({
          id: d.id,
          characterId: d.characterId,
          characterName: d.characterName,
          content: d.content,
          emotion: d.emotion || '平静',
          order: d.order || 0
        })),
        
        // 分支（保留完整信息用于节点连接）
        hasBranch: node.data.hasBranches || (node.data.branches && node.data.branches.length > 0) || false,
        isBranch: node.data.isBranch || false,
        branches: (node.data.branches || []).map(b => ({
          id: b.id,
          text: b.text,
          targetNodeId: b.targetNodeId,
          targetSceneId: b.targetSceneId,
          targetShotNumber: b.targetShotNumber || parseInt(b.targetNodeId?.split('-')[1]) || 0,
          path: b.path,
          consequence: b.consequence
        })),
        choices: node.data.choices || [],
        
        // 结局信息
        isEnding: node.data.isEnding || false,
        endingType: node.data.endingType || '',
        endingDescription: node.data.endingDescription || ''
      }
    })

    // 从故事元数据中提取其他信息
    const metadata = currentStory.metadata || {}
    
    // 提取并完善角色数据
    const characters = (currentStory.characters || []).map(char => ({
      id: char.id,
      name: char.name,
      type: char.type || char.role || 'supporting',
      role: char.role || char.type || 'supporting',
      description: char.description || '',
      appearance: char.appearance || '',
      personality: char.personality || '',
      background: char.background || '',
      // 视觉相关
      imagePrompt: char.imagePrompt || '',
      avatar: char.avatar || '',
      generatedImage: char.generatedImage || '',
      // 配音相关
      voiceType: char.voiceType || '',
      dialogueStyle: char.dialogueStyle || '',
      selectedVoice: char.selectedVoice || null,
      voiceOptions: char.voiceOptions || []
    }))
    
    // 提取并完善场景数据
    const scenes = (currentStory.scenes || []).map(scene => ({
      id: scene.id,
      name: scene.name,
      description: scene.description || '',
      atmosphere: scene.atmosphere || '',
      visualFeatures: scene.visualFeatures || '',
      imagePrompt: scene.imagePrompt || '',
      generatedImage: scene.generatedImage || '',
      musicStyle: scene.musicStyle || '',
      soundEffects: scene.soundEffects || '',
      mainEvents: scene.mainEvents || ''
    }))
    
    return {
      // 基本信息
      userDescription: currentStory.description || '',
      
      // 已确认的数据
      confirmedData: {
        genre: metadata.genre || { title: currentStory.genre || '', description: '' },
        theme: metadata.theme || { title: currentStory.theme || '', description: '' },
        style: metadata.style || { title: currentStory.style || '', description: '' },
        outline: metadata.outline || { title: currentStory.title, description: currentStory.description },
        characters: {
          type: 'characters',
          title: '角色设计',
          characters: characters
        },
        scenes: {
          type: 'scenes',
          title: '场景设计',
          scenes: scenes
        },
        storyboard: {
          type: 'storyboard',
          title: '分镜表',
          shots: shots
        }
      },
      
      // 元数据
      storyId: currentStory.id,
      storyTitle: currentStory.title,
      fromEditor: true
    }
  }

  // 返回智能引导
  const returnToGuidedCreation = async () => {
    // 先保存当前数据
    await saveCanvasData()
    
    // 转换数据格式
    const guidedData = convertEditorDataToGuidedFormat()
    
    if (!guidedData) {
      toast.error('无法转换数据格式')
      return
    }
    
    // 保存到IndexedDB供智能引导使用
    try {
      await localCloudService.saveToIndexedDB(`guided-edit-${currentStory.id}`, guidedData)
      console.log('✅ 编辑器数据已转换为智能引导格式并保存到IndexedDB:', guidedData)
      
      // 跳转回首页，带上编辑标记
      navigate(`/?edit=${currentStory.id}`)
      toast.success('已切换到智能引导模式')
    } catch (error) {
      console.error('❌ 保存智能引导数据失败:', error)
      toast.error('保存失败: ' + error.message)
    }
  }

  // 添加新节点
  const addNewNode = (position = null) => {
    const validPosition = validateAndFixNodePosition(position || { 
      x: Math.random() * 400 + 200, 
      y: Math.random() * 300 + 200 
    })
    
    const newNode = {
      id: `node-${Date.now()}`,
      position: validPosition,
      type: 'storyNode',
      data: {
        title: '新镜头',
        description: '点击编辑镜头内容',
        type: 'scene',
        content: '',
        imagePrompt: '',
        sceneImage: '',
        imageFixed: false,
        imageSource: 'none',
        branches: [],
        dialogues: [],
        selectedCharacters: []
      }
    }
    console.log('添加新节点:', newNode)
    console.log('当前节点数量:', nodes.length)
    console.log('新节点位置:', newNode.position)
    
    // 使用函数式更新添加新节点
    setNodes(nds => {
      const updatedNodes = [...nds, newNode]
      console.log('setNodes更新前:', nds.length, '更新后:', updatedNodes.length)
      console.log('新增节点详情:', updatedNodes[updatedNodes.length - 1])
      // 立即保存新节点
      setTimeout(() => {
        if (currentStory) {
          updateStory(currentStory.id, { 
            nodes: updatedNodes, 
            edges: edges 
          })
          console.log('新节点已保存到故事:', updatedNodes.length, '个节点')
        }
      }, 50)
      return updatedNodes
    })
    
    setSelectedNode(newNode)
    setShowNodeEditor(true)
    setCanvasContextMenu({ show: false, position: { x: 0, y: 0 } })
  }

  // 删除节点
  const deleteSelectedNode = () => {
    if (selectedNode) {
      setNodeToDelete(selectedNode)
      setShowDeleteConfirm(true)
    }
  }
  
  // 确认删除节点
  const confirmDeleteNode = () => {
    if (nodeToDelete) {
      // console.log('删除节点:', nodeToDelete.id)
      
      // 使用函数式更新状态
      setNodes(nds => {
        const updatedNodes = nds.filter(node => node.id !== nodeToDelete.id)
        setEdges(eds => {
          const updatedEdges = eds.filter(edge => 
            edge.source !== nodeToDelete.id && edge.target !== nodeToDelete.id
          )
          // 立即保存删除后的状态
          setTimeout(() => {
            if (currentStory) {
              updateStory(currentStory.id, { 
                nodes: updatedNodes, 
                edges: updatedEdges 
              })
              
              // 同时保存用户编辑的数据到sessionStorage（不再使用localStorage）
              try {
                const userEditedData = {
                  nodes: updatedNodes,
                  edges: updatedEdges,
                  lastEdited: new Date().toISOString()
                }
                sessionStorage.setItem(`user-edited-${currentStory.id}`, JSON.stringify(userEditedData))
              } catch (quotaError) {
                console.warn('⚠️ sessionStorage保存失败:', quotaError)
              }
              
              toast.success('节点删除成功')
            }
          }, 50)
          return updatedEdges
        })
        return updatedNodes
      })
      
      setSelectedNode(null)
      setShowNodeEditor(false)
      setShowDeleteConfirm(false)
      setNodeToDelete(null)
    }
  }
  
  // 取消删除
  const cancelDeleteNode = () => {
    setShowDeleteConfirm(false)
    setNodeToDelete(null)
  }

  // 节点选择处理 - 只选择节点，不自动打开编辑面板
  const onNodeClick = (event, node) => {
    setSelectedNode(node)
    setSelectedEdge(null) // 清除连线选择
    // 关闭右键菜单
    setCanvasContextMenu({ show: false, position: { x: 0, y: 0 }, flowPosition: { x: 0, y: 0 } })
    // 不自动打开编辑面板，用户需要右键选择编辑
  }

  // 处理右键菜单事件
  useEffect(() => {
    const handleEditNode = (event) => {
      console.log('收到编辑节点事件:', event.detail)
      const nodeId = event.detail.nodeId
      const node = nodes.find(n => n.id === nodeId)
      if (node) {
        console.log('找到要编辑的节点:', node)
        setSelectedNode(node)
        setShowNodeEditor(true)
      } else {
        console.log('未找到节点:', nodeId)
      }
    }
    
    // 添加键盘快捷键支持
    const handleKeyDown = (event) => {
      // Delete 键删除选中的节点或连线
      if (event.key === 'Delete') {
        if (selectedNode) {
          deleteSelectedNode()
        } else if (selectedEdge) {
          disconnectEdge(selectedEdge.id)
          setSelectedEdge(null)
          toast.success('连线已删除')
        }
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)

    const handleSetSceneImage = (event) => {
      const nodeId = event.detail.nodeId
      const node = nodes.find(n => n.id === nodeId)
      if (node) {
        setSelectedNode(node)
        setShowNodeEditor(true)
        // 可以在这里设置默认标签页为图像
      }
    }

         const handleSetBranches = (event) => {
       const nodeId = event.detail.nodeId
       const node = nodes.find(n => n.id === nodeId)
       if (node) {
         setSelectedNode(node)
         setShowNodeEditor(true)
         // 可以在这里设置默认标签页为分支
       }
     }
     
           const handleDeleteNode = (event) => {
        console.log('收到删除节点事件:', event.detail)
        const nodeId = event.detail.nodeId
        const node = nodes.find(n => n.id === nodeId)
        if (node) {
          console.log('找到要删除的节点:', node)
          setNodeToDelete(node)
          setShowDeleteConfirm(true)
        } else {
          console.log('未找到节点:', nodeId)
        }
      }
     
     const handleDisconnectEdge = (event) => {
       const edgeId = event.detail.edgeId
       disconnectEdge(edgeId)
     }

         window.addEventListener('editNode', handleEditNode)
     window.addEventListener('setSceneImage', handleSetSceneImage)
     window.addEventListener('setBranches', handleSetBranches)
     window.addEventListener('deleteNode', handleDeleteNode)
     window.addEventListener('disconnectEdge', handleDisconnectEdge)

                   return () => {
            window.removeEventListener('editNode', handleEditNode)
            window.removeEventListener('setSceneImage', handleSetSceneImage)
            window.removeEventListener('setBranches', handleSetBranches)
            window.removeEventListener('deleteNode', handleDeleteNode)
            window.removeEventListener('disconnectEdge', handleDisconnectEdge)
            window.removeEventListener('keydown', handleKeyDown)
          }
     }, [nodes, disconnectEdge])

  // 画布点击处理
  const onPaneClick = (event) => {
    setSelectedNode(null)
    setSelectedEdge(null)
    setShowNodeEditor(false)
  }
  
  // 点击画布其他地方时关闭右键菜单
  const handleCanvasClick = () => {
    setCanvasContextMenu({ show: false, position: { x: 0, y: 0 } })
  }
  
  // 画布右键菜单关闭处理
  const closeCanvasContextMenu = () => {
    setCanvasContextMenu({ show: false, position: { x: 0, y: 0 } })
  }

  // 画布右键菜单处理
  const onPaneContextMenu = (event) => {
    event.preventDefault()
    const rect = reactFlowWrapper.current?.getBoundingClientRect()
    if (rect && reactFlowInstance.current) {
      // 屏幕坐标（用于显示菜单）
      const screenPosition = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      }
      
      // 转换为 ReactFlow 画布坐标（用于创建节点）
      const flowPosition = reactFlowInstance.current.project({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      })
      
      setCanvasContextMenu({ 
        show: true, 
        position: screenPosition,
        flowPosition: flowPosition
      })
    }
  }

  // 星空粒子效果
  useEffect(() => {
    const createParticles = () => {
      const particlesContainer = document.getElementById('particles-editor')
      if (!particlesContainer) return

      const particleCount = 30
      
      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement('div')
        
        // 随机大小和位置
        const size = Math.random() * 2 + 0.5
        const posX = Math.random() * 100
        const posY = Math.random() * 100
        const delay = Math.random() * 5
        const duration = Math.random() * 20 + 10
        
        // 设置样式
        particle.style.cssText = `
          position: absolute;
          width: ${size}px;
          height: ${size}px;
          background: rgba(6, 182, 212, 0.4);
          border-radius: 50%;
          top: ${posY}%;
          left: ${posX}%;
          box-shadow: 0 0 ${size * 2}px rgba(6, 182, 212, 0.6);
          animation: float ${duration}s ease-in-out ${delay}s infinite;
        `
        
        particlesContainer.appendChild(particle)
      }
    }

    createParticles()
  }, [])

  if (!currentStory) {
    return <div className="text-center py-8">故事加载中...</div>
  }

  return (
    <div className="h-full flex flex-col relative">
      {/* 星空粒子背景效果 */}
      <div id="particles-editor" className="fixed inset-0 z-0 opacity-20 pointer-events-none"></div>
      <style jsx>{`
        .react-flow__edge:hover .edgebutton {
          opacity: 1;
        }
        .edgebutton-foreignobject {
          pointer-events: all;
        }
        .react-flow__edge {
          stroke-width: 6px;
          stroke: #3B82F6;
          z-index: 1000;
        }
        .react-flow__edge.selected {
          stroke: #10B981;
          stroke-width: 8px;
          filter: drop-shadow(0 0 8px #10B981);
        }
        .react-flow__edge foreignObject {
          display: none !important;
        }
        .react-flow__edge .edgebutton-foreignobject {
          display: none !important;
        }
        .react-flow__edge rect[fill="#ef4444"] {
          transition: all 0.2s ease;
        }
        .react-flow__edge rect[fill="#ef4444"]:hover {
          fill: #dc2626;
          stroke: #b91c1c;
          stroke-width: 2px;
          opacity: 1 !important;
        }
        .react-flow__pane {
          cursor: grab;
        }
        .react-flow__pane:active {
          cursor: grabbing;
        }
        .react-flow__node {
          cursor: grab;
        }
        .react-flow__node:active {
          cursor: grabbing;
        }
        .react-flow__node .react-flow__handle {
          pointer-events: all;
        }
        .react-flow__controls {
          pointer-events: all;
        }
        .react-flow__minimap {
          pointer-events: all;
        }
      `}</style>
      {/* 顶部工具栏 */}
      <div className="backdrop-blur-xl bg-black/20 border-b border-white/10 p-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={currentStory.title}
              onChange={(e) => {
                const newTitle = e.target.value
                updateStory(currentStory.id, { title: newTitle })
                setCurrentStory({ ...currentStory, title: newTitle })
              }}
              className="text-xl font-bold bg-transparent border-none outline-none text-white hover:bg-white/10 px-2 py-1 rounded"
              style={{ minWidth: '200px' }}
            />
            <Edit3 size={14} className="text-gray-400" />
          </div>
          <span className="text-sm text-dark-400">故事编辑器</span>
        </div>
        
        <div className="flex items-center gap-3">
          <button 
            onClick={returnToGuidedCreation}
            className="btn-secondary bg-gradient-to-r from-purple-600/20 to-pink-600/20 hover:from-purple-600/30 hover:to-pink-600/30 border-purple-500/30"
            title="返回智能引导，继续编辑策划案"
          >
            <Sparkles size={16} className="mr-2" />
            返回智能引导
          </button>
          
          <button 
            onClick={saveCanvasData}
            className="btn-secondary"
            title="保存当前编辑"
          >
            <Save size={16} className="mr-2" />
            保存
          </button>
          
          <button 
            onClick={() => {
              if (confirm('确定要清除所有编辑并重新加载AI生成的内容吗？')) {
                localStorage.removeItem(`user-edited-${currentStory.id}`)
                window.location.reload()
              }
            }}
            className="btn-secondary"
            title="重新加载AI内容"
          >
            <RefreshCw size={16} className="mr-2" />
            重新加载
          </button>
          
          <button 
            onClick={() => setShowCharacterEditor(true)}
            className="btn-secondary"
          >
<Edit3 size={16} className="mr-2" />
            角色管理
          </button>
          
          <button 
            onClick={() => setShowChapterManager(true)}
            className="btn-secondary"
          >
            <Edit3 size={16} className="mr-2" />
            章节管理
          </button>
          
          <button 
            onClick={() => setShowSceneManager(true)}
            className="btn-secondary"
            title="场景管理"
          >
            <Camera size={16} className="mr-2" />
            场景管理
          </button>
          
          <button 
            onClick={() => setShowMediaLibrary(true)}
            className="btn-secondary"
            title="媒资库管理"
          >
            <FolderOpen size={16} className="mr-2" />
            媒资库
          </button>
          
          <button 
            onClick={() => setShowDataRecovery(true)}
            className="btn-secondary"
            title="数据恢复工具"
          >
            <RotateCcw size={16} className="mr-2" />
            数据恢复
          </button>
          
          <button 
            onClick={() => setShowRecycleBin(true)}
            className="btn-secondary"
            title="回收站管理"
          >
            <Trash2 size={16} className="mr-2" />
            回收站
          </button>
          
          <button 
            onClick={() => setShowImportExport(true)}
            className="btn-secondary"
            title="导入导出数据"
          >
            <Download size={16} className="mr-2" />
            导入导出
          </button>
          
          <button
            onClick={() => navigate(`/player/${storyId}`)}
            className="btn-primary"
          >
            <Play size={16} className="mr-2" />
            预览
          </button>
          
          <button className="btn-primary">
            <Share2 size={16} className="mr-2" />
            发布
          </button>
        </div>
      </div>

      {/* 主要内容区域 */}
      <div className="flex-1 flex min-h-0">
        {/* 左侧侧边栏 */}
        <div className="w-64 bg-black/40 backdrop-blur-xl border-r border-white/10 p-4 space-y-4 overflow-y-auto">
          <div>
            <h3 className="font-semibold mb-2 text-white">画布工具</h3>
            <div className="space-y-2">
              <button 
                onClick={addNewNode}
                className="btn-primary w-full text-sm py-2"
              >
                <Plus size={14} className="mr-2" />
                添加镜头
              </button>
              <button 
                onClick={resetCanvasData}
                className="btn-danger w-full text-sm py-2"
              >
                <RefreshCw size={14} className="mr-2" />
                重置画布
              </button>
            </div>
          </div>
          
          {/* 连线管理 */}
          <div>
            <h3 className="font-semibold mb-2 text-white">连线操作</h3>
            <div className="text-xs text-dark-400 mb-2">
              在画布中选择连线后按 Delete 键删除
            </div>
          </div>
          
          {/* 调试信息 */}
          <div className="text-xs text-gray-400 space-y-1">
            <div>编辑状态: {showNodeEditor ? '开启' : '关闭'}</div>
            <div>选中节点: {selectedNode ? selectedNode.id : '无'}</div>
            <div>选中连线: {selectedEdge ? `${selectedEdge.source} → ${selectedEdge.target}` : '无'}</div>
            <div>删除确认: {showDeleteConfirm ? '显示' : '隐藏'}</div>
            <div>存储状态: {(() => {
              const storageInfo = checkStorageSpace()
              const color = storageInfo.usagePercent > 90 ? 'text-red-400' : 
                           storageInfo.usagePercent > 80 ? 'text-yellow-400' : 'text-green-400'
              return <span className={color}>{storageInfo.usagePercent.toFixed(1)}%</span>
            })()}</div>
          </div>


          <div>
            <h3 className="font-semibold mb-2 text-white">统计信息</h3>
            <div className="text-sm text-gray-300 space-y-1">
              <div>镜头数量: {nodes.length}</div>
              <div>分支连接: {edges.length}</div>
              <div>角色数量: {currentStory.characters?.length || 0}</div>
            </div>
          </div>

          <div className="pt-3 border-t border-white/10">
            <h3 className="font-semibold mb-2 text-white flex items-center">
              <span className="mr-2">💡</span>
              操作提示
            </h3>
            <div className="text-xs text-gray-400 space-y-2">
              <div className="flex items-start gap-2">
                <span className="text-blue-400 text-base">🖱️</span>
                <div className="flex-1">
                  <div className="text-white font-medium">按住左键拖动</div>
                  <div className="text-gray-500">在空白处：移动画布 (1.5x灵敏)</div>
                  <div className="text-gray-500">在节点上：移动节点</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-blue-400 text-base">🔍</span>
                <div className="flex-1">
                  <div className="text-white font-medium">滚轮缩放</div>
                  <div className="text-gray-500">向前放大(最大3x) / 向后缩小(最小0.1x)</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-blue-400 text-base">👆</span>
                <div className="flex-1">
                  <div className="text-white font-medium">双击空白处</div>
                  <div className="text-gray-500">放大并以该位置为中心</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-blue-400 text-base">➕</span>
                <div className="flex-1">
                  <div className="text-white font-medium">右键空白处</div>
                  <div className="text-gray-500">快速添加新镜头</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 画布区域 */}
        <div className="flex-1 relative min-h-0" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChangeCallback}
            onEdgesChange={onEdgesChangeCallback}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onEdgeClick={(event, edge) => {
              setSelectedEdge(edge)
              setSelectedNode(null) // 清除节点选择
              // 关闭右键菜单
              setCanvasContextMenu({ show: false, position: { x: 0, y: 0 }, flowPosition: { x: 0, y: 0 } })
            }}
            onSelectionChange={(elements) => {
              // 处理选中状态变化
              try {
                if (elements && Array.isArray(elements)) {
                  const selectedEdges = elements.filter(el => el && el.type === 'edge')
                  if (selectedEdges.length > 0) {
                    setSelectedEdge(selectedEdges[0])
                    setSelectedNode(null)
                  } else {
                    setSelectedEdge(null)
                  }
                } else {
                  // 如果没有选中任何元素，清除选择
                  setSelectedEdge(null)
                }
              } catch (error) {
                console.error('onSelectionChange 错误:', error)
                setSelectedEdge(null)
              }
            }}
            onPaneClick={onPaneClick}
            onPaneClickCapture={handleCanvasClick}
            onPaneContextMenu={onPaneContextMenu}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            attributionPosition="bottom-left"
            className="bg-dark-900 h-full"
            key={`reactflow-${nodes.length}-${edges.length}`}
            // 画布操作配置 - 优化交互
            nodesDraggable={true} // 允许拖动节点
            nodesConnectable={true} // 允许连接节点
            elementsSelectable={true} // 允许选择元素
            selectNodesOnDrag={false} // 禁用拖动时选中，避免冲突
            panOnDrag={[1, 2]} // 左键和中键都可以拖动画布（1=左键, 2=中键）
            panOnScroll={false} // 禁用滚动平移，只用于缩放
            zoomOnScroll={true} // 滚轮缩放：往前放大，往后缩小
            zoomOnPinch={true} // 触摸板缩放
            zoomOnDoubleClick={false} // 禁用默认双击缩放，使用自定义的
            preventScrolling={true} // 防止页面滚动
            selectionKeyCode="Shift" // 按住 Shift 多选
            zoomActivationKeyCode={null} // 不需要按键就能缩放
            minZoom={0.1} // 最小缩放比例（允许缩得更小）
            maxZoom={3} // 最大缩放比例
            defaultZoom={1} // 默认缩放比例
            // 添加拖拽事件处理器
            onNodeDragStart={onNodeDragStart}
            onNodeDrag={onNodeDrag}
            onNodeDragStop={onNodeDragStop}
            // 性能优化
            onlyRenderVisibleElements={false}
          >
            <Background color="#475569" gap={16} />
            <Controls className="bg-black/60 backdrop-blur-xl border border-white/20 rounded-2xl" />
            <MiniMap 
              className="bg-black/60 backdrop-blur-xl border border-white/20 rounded-2xl"
              nodeColor="#6B7280"
              maskColor="rgba(0, 0, 0, 0.3)"
            />
            <CanvasInteractionHandler 
              instanceRef={reactFlowInstance} 
              onInteractionStart={() => {
                // 任何交互开始时都关闭右键菜单
                setCanvasContextMenu({ show: false, position: { x: 0, y: 0 }, flowPosition: { x: 0, y: 0 } })
              }}
            />
          </ReactFlow>

          {/* 画布右键菜单 */}
          {canvasContextMenu.show && (
            <div 
              className="absolute z-50 bg-black/80 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl py-2 min-w-[200px]"
              style={{
                top: canvasContextMenu.position.y,
                left: canvasContextMenu.position.x
              }}
            >
              <button 
                className="w-full text-left px-4 py-2 text-sm text-white hover:bg-white/10 flex items-center gap-2 rounded-lg transition-colors duration-200"
                onClick={() => {
                  // 使用转换后的流坐标创建场景
                  addNewNode(canvasContextMenu.flowPosition)
                  closeCanvasContextMenu()
                }}
              >
                <Plus size={14} />
                添加镜头
              </button>
              <button 
                className="w-full text-left px-4 py-2 text-sm text-gray-400 hover:bg-white/5 cursor-not-allowed flex items-center gap-2 rounded-lg transition-colors duration-200"
                disabled
              >
                <Settings size={14} />
                画布设置
              </button>
            </div>
          )}
        </div>

        {/* 右侧节点编辑器 */}
        {showNodeEditor && selectedNode && (
          <div className="w-80 bg-black/40 backdrop-blur-xl border-l border-white/10 p-4 overflow-y-auto max-h-[calc(100vh-64px)]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">编辑镜头</h3>
              <button
                onClick={() => setShowNodeEditor(false)}
                className="text-dark-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            <div className="text-xs text-gray-400 mb-2">正在编辑节点: {selectedNode.id}</div>
            <NodeEditor 
              node={selectedNode}
              onUpdate={(updatedNode) => {
                console.log('更新节点:', updatedNode)
                setNodes(nds => {
                  const updatedNodes = nds.map(n => 
                    n.id === updatedNode.id ? updatedNode : n
                  )
                  
                  // 立即更新当前故事状态，确保数据同步
                  if (currentStory) {
                    const updatedStory = {
                      ...currentStory,
                      nodes: updatedNodes
                    }
                    setCurrentStory(updatedStory)
                    console.log('🔄 当前故事状态已更新')
                    
                    // 强制触发节点重新渲染 - 使用更有效的方式
                    setTimeout(() => {
                      setNodes(prevNodes => prevNodes.map(n => 
                        n.id === updatedNode.id ? { ...n, data: { ...n.data } } : n
                      ))
                      console.log('🔄 强制触发节点重新渲染')
                    }, 50)
                  }
                  
                  // 使用函数式更新获取最新的edges状态
                  setEdges(currentEdges => {
                    setTimeout(() => {
                      if (currentStory) {
                        updateStory(currentStory.id, { 
                          nodes: updatedNodes, 
                          edges: currentEdges 
                        })
                        
                        // 同时保存用户编辑的数据到独立存储
                        try {
                          const userEditedData = {
                            nodes: updatedNodes,
                            edges: currentEdges,
                            lastEdited: new Date().toISOString()
                          }
                          localStorage.setItem(`user-edited-${currentStory.id}`, JSON.stringify(userEditedData))
                        } catch (quotaError) {
                          console.warn('⚠️ 存储空间不足，跳过用户编辑数据保存:', quotaError)
                        }
                        
                        toast.success('场景保存成功')
                        console.log('✅ 节点数据已保存:', {
                          nodeId: updatedNode.id,
                          hasImage: !!updatedNode.data.sceneImage,
                          imageUrl: updatedNode.data.sceneImage
                        })
                        console.log('💾 用户编辑数据已独立保存')
                      }
                    }, 50)
                    return currentEdges
                  })
                  return updatedNodes
                })
              }}
              onDelete={deleteSelectedNode}
            />
          </div>
        )}
      </div>

      {/* 角色编辑器模态框 */}
      {showCharacterEditor && (
        <CharacterEditor 
          story={currentStory}
          onClose={() => setShowCharacterEditor(false)}
          onUpdate={(updatedStory) => {
            updateStory(currentStory.id, updatedStory)
            setShowCharacterEditor(false)
          }}
        />
      )}

      {/* 章节管理器模态框 */}
      {showChapterManager && (
        <ChapterManager 
          story={currentStory}
          onClose={() => setShowChapterManager(false)}
          onUpdate={(updatedStory) => {
            updateStory(currentStory.id, updatedStory)
            setShowChapterManager(false)
          }}
        />
      )}

      {/* 场景管理器模态框 */}
      {showSceneManager && (
        <SceneManager 
          story={currentStory}
          onClose={() => setShowSceneManager(false)}
          onUpdate={(updatedStory) => {
            updateStory(currentStory.id, updatedStory)
            setShowSceneManager(false)
          }}
        />
      )}

      {/* 媒资库模态框 */}
      {showMediaLibrary && (
        <MediaLibrary
          onClose={() => setShowMediaLibrary(false)}
          onSelectMedia={handleMediaSelect}
          selectedMediaId={selectedNode?.data?.imageFileName}
        />
      )}

      {/* 数据恢复模态框 */}
      {showDataRecovery && (
        <DataRecoveryModal
          isOpen={showDataRecovery}
          onClose={() => setShowDataRecovery(false)}
          storyId={currentStory?.id}
          onRecover={handleDataRecovery}
        />
      )}

      {/* 回收站模态框 */}
      {showRecycleBin && (
        <RecycleBinModal
          isOpen={showRecycleBin}
          onClose={() => setShowRecycleBin(false)}
          onRestore={handleRecycleBinRestore}
        />
      )}

      {/* 导入导出模态框 */}
      {showImportExport && (
        <ImportExportModal
          isOpen={showImportExport}
          onClose={() => setShowImportExport(false)}
          onImport={handleImportData}
        />
      )}
      
      {/* 删除确认对话框 */}
      {showDeleteConfirm && nodeToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-black/80 backdrop-blur-xl rounded-2xl p-6 w-full max-w-md border border-white/10">
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold text-white mb-2">确认删除场景</h3>
                              <p className="text-gray-300">
                你确定要删除场景 "{nodeToDelete.data.title || '未命名场景'}" 吗？
               </p>
               <p className="text-red-400 text-sm mt-2">此操作无法撤销</p>
               <p className="text-xs text-gray-400 mt-1">节点ID: {nodeToDelete.id}</p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={cancelDeleteNode}
                className="btn-secondary flex-1"
              >
                取消
              </button>
              <button
                onClick={confirmDeleteNode}
                className="btn-danger flex-1"
              >
                删除镜头
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// 节点编辑器组件
const NodeEditor = ({ node, onUpdate, onDelete }) => {
  const { currentStory, updateStory } = useStoryStore()
  const [formData, setFormData] = useState({})
  const [activeTab, setActiveTab] = useState('content') // content, image, branches, dialogues
  const [showCharacterEditor, setShowCharacterEditor] = useState(false)
  
  // 当节点变化时，重置表单数据
  React.useEffect(() => {
    if (node) {
      setFormData({
        title: node.data.title || '',
        description: node.data.description || '',
        content: node.data.content || '',
        imagePrompt: node.data.imagePrompt || '',
        sceneImage: node.data.sceneImage || '',
        branches: node.data.branches || [],
        dialogues: node.data.dialogues || [],
        selectedCharacters: node.data.selectedCharacters || []
      })
    }
  }, [node])

  const handleSave = () => {
    // 确保图片固定状态被正确保存
    const imageStatus = formData.sceneImage ? 
      ImageManager.getImageStatus(formData.sceneImage, formData.imageFixed, formData.imageSource) : 
      { imageFixed: false, imageSource: 'none' }
    
    const updatedNode = {
      ...node,
      data: {
        ...node.data,
        ...formData,
        imageFixed: imageStatus.imageFixed,
        imageSource: imageStatus.imageSource,
        hasImage: !!formData.sceneImage,
        hasDialogue: !!formData.content || (formData.dialogues && formData.dialogues.length > 0)
      }
    }
    
    console.log('✅ 场景保存，图片状态:', {
      imageUrl: formData.sceneImage,
      imageFixed: imageStatus.imageFixed,
      imageSource: imageStatus.imageSource
    })
    
    console.log('🔍 更新后的节点数据:', {
      id: updatedNode.id,
      sceneImage: updatedNode.data.sceneImage,
      backgroundImage: updatedNode.data.backgroundImage,
      hasImage: updatedNode.data.hasImage,
      imageFixed: updatedNode.data.imageFixed,
      imageSource: updatedNode.data.imageSource
    })
    
    onUpdate(updatedNode)
  }

  const addBranch = () => {
    const newBranch = {
      id: `branch-${Date.now()}`,
      text: '',
      targetNodeId: '',
      targetSceneId: '', // 添加备用字段
      chapter: 1,
      scene: 1
    }
    setFormData(prev => ({
      ...prev,
      branches: [...prev.branches, newBranch]
    }))
  }

  const removeBranch = (branchId) => {
    setFormData(prev => ({
      ...prev,
      branches: prev.branches.filter(b => b.id !== branchId)
    }))
  }

  const addDialogue = () => {
    const newDialogue = {
      id: `dialogue-${Date.now()}`,
      characterId: '',
      characterName: '',
      content: '',
      order: formData.dialogues.length + 1
    }
    setFormData(prev => ({
      ...prev,
      dialogues: [...prev.dialogues, newDialogue]
    }))
  }

  const removeDialogue = (dialogueId) => {
    setFormData(prev => ({
      ...prev,
      dialogues: prev.dialogues.filter(d => d.id !== dialogueId)
    }))
  }

  // 实时更新节点数据的函数
  const updateNodeDataInRealTime = (imageUrl, imageStatus, fileName) => {
    if (!node) return
    
    console.log('🔄 实时更新节点数据:', {
      nodeId: node.id,
      imageUrl,
      imageStatus,
      fileName
    })
    
    // 更新当前节点的数据
    const updatedNode = {
      ...node,
      data: {
        ...node.data,
        sceneImage: imageUrl,
        imageFixed: imageStatus.imageFixed,
        imageSource: imageStatus.imageSource,
        hasImage: true,
        imageFileName: fileName
      }
    }
    
    // 调用父组件的更新函数
    onUpdate(updatedNode)
    
    console.log('✅ 节点数据已实时更新')
  }

  const handleImageUpload = async (event) => {
    const file = event.target.files[0]
    if (file) {
      try {
        console.log('开始上传图片:', file.name, '大小:', file.size)
        
        // 检查文件大小（限制为5MB）
        if (file.size > 5 * 1024 * 1024) {
          toast.error('图片文件过大，请选择小于5MB的图片')
          return
        }
        
        toast.loading('正在上传图片...')
        
        // 生成唯一的文件名
        const fileName = `image-${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${file.name.split('.').pop()}`
        console.log('生成文件名:', fileName)
        
        // 保存图片到本地云存储
        console.log('开始保存图片到本地云存储...')
        const imageResult = await localCloudService.saveImageFile(fileName, file)
        console.log('图片保存结果:', imageResult)
        
        if (imageResult && imageResult.url) {
          // 直接使用保存时返回的blob URL
          const imageUrl = imageResult.url
          console.log('使用保存时返回的图片URL:', imageUrl)
          
          const imageStatus = ImageManager.getImageStatus(imageUrl, false, 'user')
          console.log('图片状态:', imageStatus)
          
          // 更新表单数据
          setFormData(prev => ({
            ...prev,
            sceneImage: imageUrl,
            imageFixed: imageStatus.imageFixed,
            imageSource: imageStatus.imageSource,
            imageFileName: fileName // 保存文件名用于后续引用
          }))
          
          // 实时更新节点数据
          updateNodeDataInRealTime(imageUrl, imageStatus, fileName)
          
          console.log('✅ 用户图片上传完成，已保存到本地云存储并标记为固定图片')
          toast.success('图片上传成功！')
        } else {
          throw new Error('图片保存失败或未返回URL')
        }
      } catch (error) {
        console.error('图片上传失败:', error)
        toast.error(`图片上传失败: ${error.message}`)
      } finally {
        // 重置文件输入，允许重复选择同一文件
        event.target.value = ''
      }
    }
  }

  const generateAIImage = async () => {
    if (!formData.imagePrompt.trim()) {
      toast.error('请输入图像提示词')
      return
    }
    
    // 检查图像生成API配置
    const imageConfig = settingsService.getImageGenerationConfig()
    const hasImageAPI = imageConfig.apiKey && imageConfig.apiUrl
    
    if (hasImageAPI) {
      // 使用真实的AI图像生成API
      toast.loading('AI正在生成图像...')
      try {
        const { RealAIService } = await import('../services/realAiService.js')
        const imageUrl = await RealAIService.generateSceneImage(
          formData.imagePrompt,
          currentStory?.characters || [],
          currentStory?.style || '现代'
        )
        const imageStatus = ImageManager.getImageStatus(imageUrl, false, 'ai')
        
        // 更新表单数据
        setFormData(prev => ({
          ...prev,
          sceneImage: imageUrl,
          imageFixed: imageStatus.imageFixed,
          imageSource: imageStatus.imageSource
        }))
        
        // 实时更新节点数据
        updateNodeDataInRealTime(imageUrl, imageStatus, null)
        
        toast.success('AI图像生成成功！')
      } catch (error) {
        console.error('AI图像生成失败:', error)
        toast.error(`图像生成失败: ${error.message}`)
      }
    } else {
      // 使用模拟图像
      toast.loading('正在生成模拟图像...')
      try {
        await new Promise(resolve => setTimeout(resolve, 1500))
        const mockImageUrl = `https://picsum.photos/400/300?random=${Date.now()}`
        const imageStatus = ImageManager.getImageStatus(mockImageUrl, false, 'system')
        
        // 更新表单数据
        setFormData(prev => ({
          ...prev,
          sceneImage: mockImageUrl,
          imageFixed: imageStatus.imageFixed,
          imageSource: imageStatus.imageSource
        }))
        
        // 实时更新节点数据
        updateNodeDataInRealTime(mockImageUrl, imageStatus, null)
        
        toast.success('模拟图像生成成功！')
      } catch (error) {
        toast.error('图像生成失败，请重试')
      }
    }
  }

  return (
    <div className="h-full flex flex-col">
      {/* 标签页导航 */}
      <div className="flex border-b border-dark-600 mb-4">
        {[
          { id: 'content', label: '镜头内容', icon: Edit3 },
          { id: 'image', label: '镜头图像', icon: Image },
          { id: 'branches', label: '分支选项', icon: MessageSquare },
          { id: 'dialogues', label: '角色对话', icon: MessageSquare }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-primary-500 text-primary-400'
                : 'border-transparent text-dark-300 hover:text-white'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* 标签页内容 */}
      <div className="flex-1 overflow-y-auto">
        {/* 镜头内容编辑 */}
        {activeTab === 'content' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white mb-2">镜头标题</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="input-field w-full"
                placeholder="输入镜头标题"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">镜头描述</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="input-field w-full h-24"
                placeholder="描述镜头的背景、环境、氛围等"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">镜头内容</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                className="input-field w-full h-32"
                placeholder="输入镜头的主要内容、事件描述等"
              />
            </div>
          </div>
        )}

        {/* 镜头图像设置 */}
        {activeTab === 'image' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white mb-2">当前镜头图</label>
              {formData.sceneImage ? (
                <div className="relative w-full h-48 bg-dark-600 rounded overflow-hidden">
                  <img 
                    src={formData.sceneImage} 
                    alt="镜头图" 
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => {
                      setFormData(prev => ({ ...prev, sceneImage: '' }))
                      // 实时更新节点数据
                      updateNodeDataInRealTime('', { imageFixed: false, imageSource: 'none' }, null)
                    }}
                    className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-1 rounded"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ) : (
                <div className="w-full h-48 bg-dark-600 rounded flex items-center justify-center border-2 border-dashed border-dark-500">
                  <div className="text-center text-dark-400">
                    <Image size={48} className="mx-auto mb-2" />
                    <p>暂无场景图</p>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">图像提示词</label>
              <textarea
                value={formData.imagePrompt}
                onChange={(e) => setFormData(prev => ({ ...prev, imagePrompt: e.target.value }))}
                className="input-field w-full h-24"
                placeholder="描述镜头图像，用于AI生成（建议使用英文）"
              />
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-dark-400">
                  AI模型提示词必须使用英文，建议详细描述镜头内容、风格、氛围等
                </p>
                <div className="flex items-center gap-1 text-xs">
                  {settingsService.getImageGenerationConfig().apiKey ? (
                    <span className="text-green-400 flex items-center gap-1">
                      <Brain size={12} />
                      AI生成
                    </span>
                  ) : (
                    <span className="text-yellow-400 flex items-center gap-1">
                      <Image size={12} />
                      模拟生成
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <label className="flex-1 cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  value=""
                />
                <div className="btn-secondary w-full text-center">
                  上传图片
                </div>
              </label>
              <button
                onClick={generateAIImage}
                className="btn-primary flex-1"
                title={settingsService.getImageGenerationConfig().apiKey ? '使用AI生成真实图像' : '使用模拟图像'}
              >
                {settingsService.getImageGenerationConfig().apiKey ? 'AI生成' : '模拟生成'}
              </button>
            </div>
          </div>
        )}

        {/* 分支选项设置 */}
        {activeTab === 'branches' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-white">玩家选择</label>
              <button
                onClick={addBranch}
                className="text-primary-400 hover:text-primary-300 text-sm flex items-center gap-1"
              >
                <Plus size={14} />
                添加选项
              </button>
            </div>
            
            <div className="space-y-3">
              {formData.branches.map((branch, index) => (
                <div key={branch.id} className="bg-dark-700 rounded-lg p-3 border border-dark-600">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-dark-400">选项 {index + 1}</span>
                    <button
                      onClick={() => removeBranch(branch.id)}
                      className="text-red-400 hover:text-red-300 ml-auto"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={branch.text}
                      onChange={(e) => {
                        const newBranches = [...formData.branches]
                        newBranches[index].text = e.target.value
                        setFormData(prev => ({ ...prev, branches: newBranches }))
                      }}
                      className="input-field w-full text-sm"
                      placeholder="输入选项文本"
                    />
                    
                    <div>
                      <label className="block text-xs text-dark-400 mb-1">目标场景</label>
                      <select
                        value={branch.targetNodeId || branch.targetSceneId || ''}
                        onChange={(e) => {
                          const newBranches = [...formData.branches]
                          newBranches[index].targetNodeId = e.target.value
                          newBranches[index].targetSceneId = e.target.value
                          setFormData(prev => ({ ...prev, branches: newBranches }))
                        }}
                        className="input-field w-full text-sm"
                      >
                        <option value="">-- 选择目标场景 --</option>
                        {currentStory?.nodes?.map(node => (
                          <option key={node.id} value={node.id}>
                            {node.data?.title || `节点 ${node.id}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-dark-400 mb-1">章节</label>
                        <select
                          value={branch.chapter}
                          onChange={(e) => {
                            const newBranches = [...formData.branches]
                            newBranches[index].chapter = parseInt(e.target.value)
                            setFormData(prev => ({ ...prev, branches: newBranches }))
                          }}
                          className="input-field w-full text-sm"
                        >
                          {[1, 2, 3, 4, 5].map(num => (
                            <option key={num} value={num}>{num}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-xs text-dark-400 mb-1">场景</label>
                        <select
                          value={branch.scene}
                          onChange={(e) => {
                            const newBranches = [...formData.branches]
                            newBranches[index].scene = parseInt(e.target.value)
                            setFormData(prev => ({ ...prev, branches: newBranches }))
                          }}
                          className="input-field w-full text-sm"
                        >
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                            <option key={num} value={num}>{num}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

                 {/* 角色对话设置 */}
         {activeTab === 'dialogues' && (
           <div className="space-y-4">
             <div className="flex items-center justify-between">
               <label className="block text-sm font-medium text-white">角色对话</label>
               <div className="flex gap-2">
                 <button
                   onClick={() => setShowCharacterEditor(true)}
                   className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                 >
                   <Edit3 size={14} />
                   管理角色
                 </button>
                 <button
                   onClick={addDialogue}
                   className="text-primary-400 hover:text-primary-300 text-sm flex items-center gap-1"
                 >
                   <Plus size={14} />
                   添加对话
                 </button>
               </div>
             </div>
             
             <div className="space-y-3">
               {formData.dialogues.map((dialogue, index) => (
                 <div key={dialogue.id} className="bg-dark-700 rounded-lg p-3 border border-dark-600">
                   <div className="flex items-center gap-2 mb-2">
                     <span className="text-xs text-dark-400">对话 {index + 1}</span>
                     <button
                       onClick={() => removeDialogue(dialogue.id)}
                       className="text-red-400 hover:text-red-300 ml-auto"
                     >
                       <Trash2 size={14} />
                     </button>
                   </div>
                   
                   <div className="space-y-2">
                     {/* 角色选择 */}
                     <div>
                       <label className="block text-xs text-dark-400 mb-1">选择角色</label>
                       <select
                         value={dialogue.characterId || ''}
                         onChange={(e) => {
                           const newDialogues = [...formData.dialogues]
                           const selectedCharacter = currentStory?.characters?.find(c => c.id === e.target.value)
                           newDialogues[index].characterId = e.target.value
                           newDialogues[index].characterName = selectedCharacter?.name || ''
                           setFormData(prev => ({ ...prev, dialogues: newDialogues }))
                         }}
                         className="input-field w-full text-sm"
                       >
                         <option value="">-- 选择角色 --</option>
                         {currentStory?.characters?.map(character => (
                           <option key={character.id} value={character.id}>
                             {character.name} ({character.role === 'main' ? '主角' : character.role === 'supporting' ? '配角' : 'NPC'})
                           </option>
                         ))}
                       </select>
                     </div>
                     
                     <textarea
                       value={dialogue.content}
                       onChange={(e) => {
                         const newDialogues = [...formData.dialogues]
                         newDialogues[index].content = e.target.value
                         setFormData(prev => ({ ...prev, dialogues: newDialogues }))
                       }}
                       className="input-field w-full text-sm h-20"
                       placeholder="对话内容"
                     />
                   </div>
                 </div>
               ))}
             </div>
           </div>
         )}
      </div>

      {/* 底部操作按钮 */}
      <div className="flex gap-2 pt-4 border-t border-dark-600">
        <button
          onClick={handleSave}
          className="btn-primary flex-1"
        >
          <Save size={16} className="mr-2" />
          保存场景
        </button>
                 <button
           onClick={onDelete}
           className="btn-danger"
         >
           <Trash2 size={16} className="mr-2" />
           删除
         </button>
       </div>
       
       {/* 角色编辑器模态框 */}
       {showCharacterEditor && (
         <CharacterEditor 
           story={currentStory}
           onClose={() => setShowCharacterEditor(false)}
           onUpdate={(updatedStory) => {
             // 更新故事中的角色信息
             if (currentStory) {
               updateStory(currentStory.id, updatedStory)
             }
             setShowCharacterEditor(false)
           }}
         />
       )}
     </div>
   )
 }

// 角色编辑器组件
const CharacterEditor = ({ story, onClose, onUpdate }) => {
  const [characters, setCharacters] = useState(story.characters || [])
  const [editingCharacter, setEditingCharacter] = useState(null)
  const [showAddForm, setShowAddForm] = useState(false)

  const addCharacter = () => {
    const newCharacter = {
      id: `char-${Date.now()}`,
      name: '',
      type: 'supporting', // main, supporting, npc
      role: 'supporting',
      appearance: '',
      background: '',
      personality: '',
      description: '',
      // 视觉相关
      imagePrompt: '',
      avatar: '',
      generatedImage: '',
      // 配音相关
      voiceType: '',
      selectedVoice: null,
      voiceOptions: []
    }
    setEditingCharacter(newCharacter)
    setShowAddForm(true)
  }

  const saveCharacter = (character) => {
    let updatedCharacters
    if (character.id.startsWith('char-') && !characters.find(c => c.id === character.id)) {
      // 新角色
      updatedCharacters = [...characters, character]
    } else {
      // 更新现有角色
      updatedCharacters = characters.map(c => c.id === character.id ? character : c)
    }
    
    setCharacters(updatedCharacters)
    onUpdate({ characters: updatedCharacters })
    setEditingCharacter(null)
    setShowAddForm(false)
    toast.success('角色保存成功')
  }

  const deleteCharacter = (characterId) => {
    if (window.confirm('确定要删除这个角色吗？')) {
      const updatedCharacters = characters.filter(c => c.id !== characterId)
      setCharacters(updatedCharacters)
      onUpdate({ characters: updatedCharacters })
      toast.success('角色删除成功')
    }
  }

  const getRoleColor = (role) => {
    switch (role) {
      case 'main': return 'bg-primary-600'
      case 'supporting': return 'bg-blue-600'
      case 'npc': return 'bg-gray-600'
      default: return 'bg-gray-600'
    }
  }

  const getRoleLabel = (role) => {
    switch (role) {
      case 'main': return '主角'
      case 'supporting': return '配角'
      case 'npc': return 'NPC'
      default: return 'NPC'
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-dark-800 rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-white">角色管理</h3>
          <div className="flex gap-2">
            <button
              onClick={addCharacter}
              className="btn-primary"
            >
              <Plus size={16} className="mr-2" />
              添加角色
            </button>
            <button onClick={onClose} className="btn-secondary">关闭</button>
          </div>
        </div>

        {/* 角色列表 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {characters.map(character => (
            <div key={character.id} className="bg-dark-700 rounded-lg p-4 border border-dark-600">
              {/* 立绘图预览 */}
              {(character.avatar || character.generatedImage) && (
                <div className="mb-3 flex justify-center">
                  <img 
                    src={character.avatar || character.generatedImage} 
                    alt={character.name} 
                    className="w-24 h-32 object-cover rounded-lg border-2 border-primary-500"
                  />
                </div>
              )}
              
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full ${getRoleColor(character.role || character.type)} flex items-center justify-center text-white text-xs font-bold`}>
                    {character.name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">{character.name || '未命名角色'}</h4>
                    <span className={`text-xs px-2 py-1 rounded-full ${getRoleColor(character.role || character.type)} text-white`}>
                      {getRoleLabel(character.role || character.type)}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => {
                      setEditingCharacter(character)
                      setShowAddForm(true)
                    }}
                    className="text-blue-400 hover:text-blue-300 p-1"
                  >
                    <Edit3 size={14} />
                  </button>
                  <button
                    onClick={() => deleteCharacter(character.id)}
                    className="text-red-400 hover:text-red-300 p-1"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-dark-400">外观：</span>
                  <p className="text-white line-clamp-2">{character.appearance || character.description || '未设置'}</p>
                </div>
                <div>
                  <span className="text-dark-400">背景：</span>
                  <p className="text-white line-clamp-2">{character.background || '未设置'}</p>
                </div>
                <div>
                  <span className="text-dark-400">性格：</span>
                  <p className="text-white line-clamp-2">{character.personality || '未设置'}</p>
                </div>
                
                {/* 显示文生图关键词 */}
                {character.imagePrompt && (
                  <div>
                    <span className="text-dark-400">立绘关键词：</span>
                    <p className="text-primary-400 text-xs line-clamp-1">{character.imagePrompt}</p>
                  </div>
                )}
                
                {/* 显示配音类型 */}
                {character.voiceType && (
                  <div>
                    <span className="text-dark-400">配音：</span>
                    <p className="text-green-400 text-xs">{character.voiceType}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* 添加/编辑角色表单 */}
        {showAddForm && (
          <div className="bg-dark-700 rounded-lg p-6 border border-dark-600">
            <h4 className="text-lg font-semibold text-white mb-4">
              {editingCharacter?.id.startsWith('char-') ? '添加新角色' : '编辑角色'}
            </h4>
            
            <CharacterForm
              character={editingCharacter}
              onSave={saveCharacter}
              onCancel={() => {
                setEditingCharacter(null)
                setShowAddForm(false)
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

// 角色表单组件
const CharacterForm = ({ character, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: character?.name || '',
    type: character?.type || 'supporting',
    role: character?.role || 'supporting',
    appearance: character?.appearance || '',
    background: character?.background || '',
    personality: character?.personality || '',
    description: character?.description || '',
    // 视觉相关
    imagePrompt: character?.imagePrompt || '',
    avatar: character?.avatar || '',
    generatedImage: character?.generatedImage || '',
    // 配音相关
    voiceType: character?.voiceType || '',
    dialogueStyle: character?.dialogueStyle || ''
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!formData.name.trim()) {
      toast.error('请输入角色名称')
      return
    }
    
    const characterData = {
      ...character,
      ...formData,
      updatedAt: new Date().toISOString()
    }
    
    if (!character?.id || character.id.startsWith('char-')) {
      characterData.id = `char-${Date.now()}`
      characterData.createdAt = new Date().toISOString()
    }
    
    onSave(characterData)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-white mb-2">角色名称 *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            className="input-field w-full"
            placeholder="输入角色名称"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-white mb-2">角色类型</label>
          <select
            value={formData.role}
            onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
            className="input-field w-full"
          >
            <option value="main">主角</option>
            <option value="supporting">配角</option>
            <option value="npc">NPC</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-white mb-2">外观描述</label>
        <textarea
          value={formData.appearance}
          onChange={(e) => setFormData(prev => ({ ...prev, appearance: e.target.value }))}
          className="input-field w-full h-20"
          placeholder="描述角色的外观特征，如身高、体型、服装等"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-white mb-2">背景故事</label>
        <textarea
          value={formData.background}
          onChange={(e) => setFormData(prev => ({ ...prev, background: e.target.value }))}
          className="input-field w-full h-20"
          placeholder="描述角色的背景故事、经历、动机等"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-white mb-2">性格特点</label>
        <textarea
          value={formData.personality}
          onChange={(e) => setFormData(prev => ({ ...prev, personality: e.target.value }))}
          className="input-field w-full h-20"
          placeholder="描述角色的性格、习惯、说话方式等"
        />
      </div>

      {/* 视觉设计区域 */}
      <div className="border-t border-dark-600 pt-4 mt-4">
        <h4 className="text-sm font-semibold text-white mb-3">🎨 视觉设计</h4>
        
        <div>
          <label className="block text-sm font-medium text-white mb-2">文生图关键词 (Image Prompt)</label>
          <textarea
            value={formData.imagePrompt}
            onChange={(e) => setFormData(prev => ({ ...prev, imagePrompt: e.target.value }))}
            className="input-field w-full h-20"
            placeholder="英文关键词，用于AI生成角色立绘。例如: portrait, young woman, long hair, fantasy style"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">头像图片URL</label>
            <input
              type="text"
              value={formData.avatar}
              onChange={(e) => setFormData(prev => ({ ...prev, avatar: e.target.value }))}
              className="input-field w-full"
              placeholder="输入图片URL或上传图片"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">生成的立绘图URL</label>
            <input
              type="text"
              value={formData.generatedImage}
              onChange={(e) => setFormData(prev => ({ ...prev, generatedImage: e.target.value }))}
              className="input-field w-full"
              placeholder="AI生成的角色立绘图URL"
            />
          </div>
        </div>

        {/* 图片预览 */}
        {(formData.avatar || formData.generatedImage) && (
          <div className="mt-3 flex gap-3">
            {formData.avatar && (
              <div>
                <p className="text-xs text-dark-400 mb-1">头像预览</p>
                <img src={formData.avatar} alt="Avatar" className="w-20 h-20 rounded-full object-cover border-2 border-dark-600" />
              </div>
            )}
            {formData.generatedImage && (
              <div>
                <p className="text-xs text-dark-400 mb-1">立绘预览</p>
                <img src={formData.generatedImage} alt="Character" className="w-20 h-28 rounded object-cover border-2 border-dark-600" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* 配音设计区域 */}
      <div className="border-t border-dark-600 pt-4 mt-4">
        <h4 className="text-sm font-semibold text-white mb-3">🎤 配音设计</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">配音类型</label>
            <input
              type="text"
              value={formData.voiceType}
              onChange={(e) => setFormData(prev => ({ ...prev, voiceType: e.target.value }))}
              className="input-field w-full"
              placeholder="例如: 成熟男声、少女声、老年声"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">对话风格</label>
            <input
              type="text"
              value={formData.dialogueStyle}
              onChange={(e) => setFormData(prev => ({ ...prev, dialogueStyle: e.target.value }))}
              className="input-field w-full"
              placeholder="例如: 温柔、冷酷、幽默"
            />
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <button type="submit" className="btn-primary flex-1">
          <Save size={16} className="mr-2" />
          保存角色
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary flex-1">
          取消
        </button>
      </div>
    </form>
  )
}

// 场景管理器组件
const SceneManager = ({ story, onClose, onUpdate }) => {
  const [scenes, setScenes] = useState(story.scenes || [])
  const [editingScene, setEditingScene] = useState(null)
  const [showAddForm, setShowAddForm] = useState(false)

  const addScene = () => {
    const newScene = {
      id: `scene-${Date.now()}`,
      name: '',
      description: '',
      atmosphere: '',
      visualFeatures: '',
      imagePrompt: '',
      generatedImage: '',
      musicStyle: '',
      soundEffects: '',
      mainEvents: ''
    }
    setEditingScene(newScene)
    setShowAddForm(true)
  }

  const saveScene = (scene) => {
    let updatedScenes
    if (scene.id.startsWith('scene-') && !scenes.find(s => s.id === scene.id)) {
      updatedScenes = [...scenes, scene]
    } else {
      updatedScenes = scenes.map(s => s.id === scene.id ? scene : s)
    }
    
    setScenes(updatedScenes)
    onUpdate({ scenes: updatedScenes })
    setEditingScene(null)
    setShowAddForm(false)
    toast.success('场景保存成功')
  }

  const deleteScene = (sceneId) => {
    if (window.confirm('确定要删除这个场景吗？')) {
      const updatedScenes = scenes.filter(s => s.id !== sceneId)
      setScenes(updatedScenes)
      onUpdate({ scenes: updatedScenes })
      toast.success('场景删除成功')
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-dark-800 rounded-lg p-6 w-full max-w-6xl max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-white">场景管理</h3>
          <div className="flex gap-2">
            <button onClick={addScene} className="btn-primary">
              <Plus size={16} className="mr-2" />
              添加场景
            </button>
            <button onClick={onClose} className="btn-secondary">关闭</button>
          </div>
        </div>

        {/* 场景列表 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {scenes.map(scene => (
            <div key={scene.id} className="bg-dark-700 rounded-lg p-4 border border-dark-600">
              {/* 场景图片预览 */}
              {scene.generatedImage && (
                <div className="mb-3">
                  <img 
                    src={scene.generatedImage} 
                    alt={scene.name} 
                    className="w-full h-32 object-cover rounded-lg"
                  />
                </div>
              )}
              
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-semibold text-white">{scene.name || '未命名场景'}</h4>
                  <span className="text-xs text-primary">
                    {scene.atmosphere || '未设置氛围'}
                  </span>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => {
                      setEditingScene(scene)
                      setShowAddForm(true)
                    }}
                    className="text-blue-400 hover:text-blue-300 p-1"
                  >
                    <Edit3 size={14} />
                  </button>
                  <button
                    onClick={() => deleteScene(scene.id)}
                    className="text-red-400 hover:text-red-300 p-1"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-dark-400">描述：</span>
                  <p className="text-white line-clamp-2">{scene.description || '未设置'}</p>
                </div>
                <div>
                  <span className="text-dark-400">视觉特征：</span>
                  <p className="text-white line-clamp-1">{scene.visualFeatures || '未设置'}</p>
                </div>
                <div>
                  <span className="text-dark-400">音乐：</span>
                  <p className="text-white line-clamp-1">{scene.musicStyle || '未设置'}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 添加/编辑场景表单 */}
        {showAddForm && (
          <div className="bg-dark-700 rounded-lg p-6 border border-dark-600">
            <h4 className="text-lg font-semibold text-white mb-4">
              {editingScene?.id.startsWith('scene-') ? '添加新场景' : '编辑场景'}
            </h4>
            
            <SceneForm
              scene={editingScene}
              onSave={saveScene}
              onCancel={() => {
                setEditingScene(null)
                setShowAddForm(false)
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

// 场景表单组件
const SceneForm = ({ scene, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: scene?.name || '',
    description: scene?.description || '',
    atmosphere: scene?.atmosphere || '',
    visualFeatures: scene?.visualFeatures || '',
    imagePrompt: scene?.imagePrompt || '',
    generatedImage: scene?.generatedImage || '',
    musicStyle: scene?.musicStyle || '',
    soundEffects: scene?.soundEffects || '',
    mainEvents: scene?.mainEvents || ''
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!formData.name.trim()) {
      toast.error('请输入场景名称')
      return
    }
    
    const sceneData = {
      ...scene,
      ...formData,
      updatedAt: new Date().toISOString()
    }
    
    if (!scene?.id || scene.id.startsWith('scene-')) {
      sceneData.id = `scene-${Date.now()}`
      sceneData.createdAt = new Date().toISOString()
    }
    
    onSave(sceneData)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-white mb-2">场景名称 *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            className="input-field w-full"
            placeholder="输入场景名称"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-white mb-2">氛围</label>
          <input
            type="text"
            value={formData.atmosphere}
            onChange={(e) => setFormData(prev => ({ ...prev, atmosphere: e.target.value }))}
            className="input-field w-full"
            placeholder="例如: 紧张、温馨、神秘"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-white mb-2">场景描述</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          className="input-field w-full h-20"
          placeholder="描述场景的整体环境和感觉"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-white mb-2">视觉特征</label>
        <textarea
          value={formData.visualFeatures}
          onChange={(e) => setFormData(prev => ({ ...prev, visualFeatures: e.target.value }))}
          className="input-field w-full h-20"
          placeholder="描述场景的视觉元素，如颜色、光影、建筑风格等"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-white mb-2">主要情节</label>
        <textarea
          value={formData.mainEvents}
          onChange={(e) => setFormData(prev => ({ ...prev, mainEvents: e.target.value }))}
          className="input-field w-full h-20"
          placeholder="这个场景中发生的主要事件"
        />
      </div>

      {/* 视觉设计 */}
      <div className="border-t border-dark-600 pt-4 mt-4">
        <h4 className="text-sm font-semibold text-white mb-3">🎨 视觉设计</h4>
        
        <div>
          <label className="block text-sm font-medium text-white mb-2">文生图关键词</label>
          <textarea
            value={formData.imagePrompt}
            onChange={(e) => setFormData(prev => ({ ...prev, imagePrompt: e.target.value }))}
            className="input-field w-full h-20"
            placeholder="英文关键词，用于AI生成场景图。例如: fantasy castle, night scene, moonlight"
          />
        </div>

        <div className="mt-3">
          <label className="block text-sm font-medium text-white mb-2">生成的场景图URL</label>
          <input
            type="text"
            value={formData.generatedImage}
            onChange={(e) => setFormData(prev => ({ ...prev, generatedImage: e.target.value }))}
            className="input-field w-full"
            placeholder="AI生成的场景图URL"
          />
        </div>

        {formData.generatedImage && (
          <div className="mt-3">
            <p className="text-xs text-dark-400 mb-1">场景图预览</p>
            <img 
              src={formData.generatedImage} 
              alt="Scene" 
              className="w-full h-40 object-cover rounded-lg border-2 border-dark-600" 
            />
          </div>
        )}
      </div>

      {/* 音效设计 */}
      <div className="border-t border-dark-600 pt-4 mt-4">
        <h4 className="text-sm font-semibold text-white mb-3">🎵 音效设计</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">音乐风格</label>
            <input
              type="text"
              value={formData.musicStyle}
              onChange={(e) => setFormData(prev => ({ ...prev, musicStyle: e.target.value }))}
              className="input-field w-full"
              placeholder="例如: 紧张的背景音乐、轻快的旋律"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">音效</label>
            <input
              type="text"
              value={formData.soundEffects}
              onChange={(e) => setFormData(prev => ({ ...prev, soundEffects: e.target.value }))}
              className="input-field w-full"
              placeholder="例如: 风声、脚步声、环境音"
            />
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <button type="submit" className="btn-primary flex-1">
          <Save size={16} className="mr-2" />
          保存场景
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary flex-1">
          取消
        </button>
      </div>
    </form>
  )
}

// 章节管理器组件
const ChapterManager = ({ story, onClose, onUpdate }) => {
  const [chapters, setChapters] = useState(story.chapters || [])
  const [editingChapter, setEditingChapter] = useState(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [selectedChapter, setSelectedChapter] = useState(null)

  const addChapter = () => {
    const newChapter = {
      id: `chapter-${Date.now()}`,
      title: '',
      description: '',
      scenes: [],
      order: chapters.length + 1
    }
    setEditingChapter(newChapter)
    setShowAddForm(true)
  }

  const saveChapter = (chapter) => {
    let updatedChapters
    if (chapter.id.startsWith('chapter-') && !chapters.find(c => c.id === chapter.id)) {
      updatedChapters = [...chapters, chapter]
    } else {
      updatedChapters = chapters.map(c => c.id === chapter.id ? chapter : c)
    }
    
    setChapters(updatedChapters)
    onUpdate({ chapters: updatedChapters })
    setEditingChapter(null)
    setShowAddForm(false)
    toast.success('章节保存成功')
  }

  const deleteChapter = (chapterId) => {
    if (window.confirm('确定要删除这个章节吗？')) {
      const updatedChapters = chapters.filter(c => c.id !== chapterId)
      setChapters(updatedChapters)
      onUpdate({ chapters: updatedChapters })
      toast.success('章节删除成功')
    }
  }

  const jumpToScene = (sceneId) => {
    // 这里可以跳转到画布中的特定场景节点
    toast.success(`跳转到场景: ${sceneId}`)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-dark-800 rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-white">章节管理</h3>
          <div className="flex gap-2">
            <button onClick={addChapter} className="btn-primary">
              <Plus size={16} className="mr-2" />
              添加章节
            </button>
            <button onClick={onClose} className="btn-secondary">关闭</button>
          </div>
        </div>

        {/* 章节列表 */}
        <div className="space-y-4 mb-6">
          {chapters.map(chapter => (
            <div key={chapter.id} className="bg-dark-700 rounded-lg p-4 border border-dark-600">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-white text-lg">{chapter.title || '未命名章节'}</h4>
                  <p className="text-dark-300 text-sm">{chapter.description || '暂无描述'}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-dark-400">
                    <span>章节 {chapter.order}</span>
                    <span>镜头数量: {chapter.scenes?.length || 0}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => {
                      setEditingChapter(chapter)
                      setShowAddForm(true)
                    }}
                    className="text-blue-400 hover:text-blue-300 p-1"
                  >
                    <Edit3 size={14} />
                  </button>
                  <button
                    onClick={() => deleteChapter(chapter.id)}
                    className="text-red-400 hover:text-red-300 p-1"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              
              {/* 场景列表 */}
              {chapter.scenes && chapter.scenes.length > 0 && (
                <div className="mt-3 pt-3 border-t border-dark-600">
                  <h5 className="text-sm font-medium text-white mb-2">场景列表</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {chapter.scenes.map(scene => (
                      <button
                        key={scene.id}
                        onClick={() => jumpToScene(scene.id)}
                        className="text-left p-2 bg-dark-600 rounded hover:bg-dark-500 transition-colors"
                      >
                        <div className="text-white text-sm font-medium">{scene.title}</div>
                        <div className="text-dark-300 text-xs">{scene.description}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* 添加/编辑章节表单 */}
        {showAddForm && (
          <div className="bg-dark-700 rounded-lg p-6 border border-dark-600">
            <h4 className="text-lg font-semibold text-white mb-4">
              {editingChapter?.id.startsWith('chapter-') ? '添加新章节' : '编辑章节'}
            </h4>
            
            <ChapterForm
              chapter={editingChapter}
              onSave={saveChapter}
              onCancel={() => {
                setEditingChapter(null)
                setShowAddForm(false)
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

// 章节表单组件
const ChapterForm = ({ chapter, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: chapter?.title || '',
    description: chapter?.description || '',
    order: chapter?.order || 1
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!formData.title.trim()) {
      toast.error('请输入章节标题')
      return
    }
    
    const chapterData = {
      ...chapter,
      ...formData,
      updatedAt: new Date().toISOString()
    }
    
    if (!chapter?.id || chapter.id.startsWith('chapter-')) {
      chapterData.id = `chapter-${Date.now()}`
      chapterData.createdAt = new Date().toISOString()
      chapterData.scenes = []
    }
    
    onSave(chapterData)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-white mb-2">章节标题 *</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          className="input-field w-full"
          placeholder="输入章节标题"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-white mb-2">章节描述</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          className="input-field w-full h-20"
          placeholder="描述章节的主要内容"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-white mb-2">章节顺序</label>
        <input
          type="number"
          value={formData.order}
          onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 1 }))}
          className="input-field w-full"
          min="1"
        />
      </div>

      <div className="flex gap-3 pt-4">
        <button type="submit" className="btn-primary flex-1">
          <Save size={16} className="mr-2" />
          保存章节
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary flex-1">
          取消
        </button>
      </div>
    </form>
  )
}
